import { AxiosResponse } from 'axios'

import { CountryCode } from '@app/consts/Countries'
import { overrideKoToKrLanguage } from '@app/context/SessionSettingsContext'

import { ForgotPasswordFormValues } from '@pages/ForgotPassword/ForgotPasswordForm'
import { ResetPasswordCompleteFormValues } from '@pages/ForgotPassword/ResetPassword.page'
import {
  AvailableDashboardTypeDto,
  ClientDashboardLayoutDto,
  CreateDashboardLayoutPayload,
  UpdateDashboardLayoutPayload,
} from '@pages/IntroducingBroker/DashboardPoC/dashboardApi.types'
import { GroupFormValues } from '@pages/IntroducingBroker/Rebates/Group.modal'
import { LoginFormValues } from '@pages/Login/LoginForm'
import { CommunicationLanguageFormValues } from '@pages/Profile/AccountSettings/CommunicationLanguageForm'
import { DateFormatFormValues } from '@pages/Profile/AccountSettings/DateFormatsForm'
import { ChangePasswordFormValues } from '@pages/Profile/AccountSettings/SecuritySettings/ChangePassword/ChangePasswordForm'
import {
  RewardProgramDetailsDto,
  RewardProgramDto,
  RewardProgramEligibilityCheckDto,
} from '@pages/RewardsHub/RewardHubDto'
import { ClientRegisterValues } from '@pages/Signup/types'
import { VideoData } from '@pages/Tools/Platforms/PlatformsTradingViewPage'
import {
  DxtradeCashTransfersQuery,
  prepareDxtradeCashTransfersParams,
} from '@pages/Trade/Portfolio/utils/DxtradeCashTransfersFields'
import { ChangeTradingAccountPasswordValues } from '@pages/Traders-Room/Trading-Accounts/ChangeTradingAccountPassword/ChangeTradingAccountPassword.page'
import { DemoTradingAccountDepositFormValues } from '@pages/Traders-Room/Trading-Accounts/Deposit/DemoTradingAccountDeposit/DemoTradingAccountDepositForm'

import {
  AccountDetailedDto,
  ClientPhoneNumberDto,
  PhoneNumberAddValues,
} from '@model/AccountDetailedDto'
import { AccountGroupType } from '@model/AccountGroupType'
import { AccountSummaryDto } from '@model/AccountSummaryDto'
import { AddressAutocompleteResponse, PlaceAddressDetails } from '@model/AddressAutocomplete'
import { AgreedClientDocumentDto } from '@model/AgreedClientDocumentDto'
import {
  FallersDto,
  MostFollowedDto,
  MostVolatileDto,
  RisersDto,
  TopTradedBoughtDto,
  TopTradedSoldDto,
} from '@model/AnalyticsDto'
import { BankAccount, BankAccountCreate, BankAccountValidationOptions } from '@model/BankAccount'
import { BonusTransactionDto } from '@model/BonusTransactionDto'
import { BrokerSummaryDto } from '@model/BrokerSummaryDto'
import { ClientRebateCampaignDto } from '@model/CampaignResultDto'
import { CardDetailsDto, CardTokenIdResponseDto } from '@model/CardDetailsDto'
import { ClientAccountsConfigurationDto } from '@model/ClientAccountsConfigurationDto'
import { ClientAreaLanguageDto } from '@model/ClientAreaLanguageDto'
import { ClientCampaignDto, MarginBonusDto } from '@model/ClientCampaignDto'
import { ClientCampaignRebateResultDto } from '@model/ClientCampaignRebateResultDto'
import {
  ClientContestCampaignDetailDto,
  DrawdownDto,
  TradingDaysPerCampaignDto,
} from '@model/ClientContestCampaignDetailDto'
import { ClientContestCampaignResultDto } from '@model/ClientContestCampaignResultDto'
import { ClientGuideDto } from '@model/ClientGuideDto'
import { ClientIbContestCampaignDetailDto } from '@model/ClientIbContestCampaignDetailDto'
import { ClientIbContestCampaignDto } from '@model/ClientIbContestCampaignDto'
import { ClientIbContestCampaignResultDto } from '@model/ClientIbContestCampaignResultDto'
import { ClientJourneyMapDto, ClientJourneyMapSummaryDto } from '@model/ClientJourneyMap'
import { ClientNciPriorityDto } from '@model/ClientNciPriorityDto'
import { ClientRebateCampaignDetailDto } from '@model/ClientRebateCampaignDetailDto'
import { TestAnswersDto } from '@model/ClientTestAnswerDto'
import { ClientTestDto } from '@model/ClientTestDto'
import { CompanyLegalDocumentDto, LegalDocumentDto } from '@model/CompanyLegalDocumentDto'
import { ContinueRegistrationValues } from '@model/ContinueRegistration'
import { CorporateActionDto } from '@model/CorporateActionDto'
import { CountryPreferenceStrippedDto, TruliooEIdData } from '@model/CountryDto'
import { CreateClientDocuments } from '@model/CreateClientDocuments'
import { CreateClientTest } from '@model/CreateClientTest'
import { CreateDormantUpdateTicket } from '@model/CreateDormantUpdateTicket'
import { CreateKycUpdateTicket } from '@model/CreateKycUpdateTicket'
import { CreateLead } from '@model/CreateLead'
import { ClientCreateNciFinalHistory, ClientCreateNciHistory } from '@model/CreateNciHistory'
import {
  CreatePhoneNumberVerification,
  PhoneVerificationWorkflowEnum,
  RestartPhoneNumberVerification,
} from '@model/CreatePhoneNumberVerification'
import {
  CreateCampaignTradingAccount,
  CreateDemoTradingAccount,
  CreateTradingAccount,
} from '@model/CreateTradingAccount'
import { CreateTradingAccountBalanceConversionTransaction } from '@model/CreateTradingAccountBalanceConversionTransaction'
import { CreateTradingAccountIntroducingBrokerRebateGroupBulk } from '@model/CreateTradingAccountIntroducingBrokerRebateGroupBulk'
import { CreateWalletDto } from '@model/CreateWalletDto'
import { CustomerSupportContact } from '@model/CustomerSupportContact'
import { DealDto } from '@model/DealDto'
import { DocumentCategorySizeDto } from '@model/DocumentCategories'
import { DocumentClientAreaDto } from '@model/DocumentClientAreaDto'
import { DocumentCategoryDto, DocumentDto } from '@model/DocumentDto'
import { DocumentGeneralStatusDto } from '@model/DocumentGeneralStatusDto'
import type {
  BulkUpdateWatchlistOrderValues,
  CreateWatchlistModel,
  DxTradeAccountMetricsResponse,
  DxTradeConversionRatesApiResponse,
  DxTradeOrderCreateResponse,
  DxTradeSingleOrderRequest,
  DxtradeWatchlistsQuery,
  MarketDataList,
  MarketDataRequest,
  UpdateWatchlistModel,
  WatchlistModel,
} from '@model/DxTrade.types'
import {
  AssetClassMarginRequirementDto,
  DxTradeAccountTransfersResponse,
  DxTradeInstrumentDetails,
  DxTradeInstrumentDetailsResponse,
  DxTradeInstrumentDetailsResponseItem,
  DxTradeInstrumentResponse,
  DxTradeOpenPositionsResponse,
  DxTradeOrdersResponse,
  DxTradeRawCandle,
  DxTradeTradingInfoDto,
  DxtradeInstrumentQuery,
} from '@model/DxTrade.types'
import { prepareDxtradeInstrumentQueryParams } from '@model/DxtradeInstrumentFields'
import {
  DxtradeOrdersHistoryQuery,
  prepareDxtradeOrdersHistoryParams,
} from '@model/DxtradeOrderHistoryFields'
import { EntityConfigurationDto } from '@model/EntityConfigurationDto'
import { ExchangeRateResponseDto } from '@model/ExchangeRateResponseDto'
import { ExportHubDto, ExportHubPayload } from '@model/ExportHub'
import { FilterOptionsDto } from '@model/FilterOptionsDto'
import { FilterOptionsForIntroducingBrokerRebatePayoutsDto } from '@model/FilterOptionsForIntroducingBrokerRebatePayoutsDto'
import { FilterOptionsForIntroducingBrokerRebatesDto } from '@model/FilterOptionsForIntroducingBrokerRebatesDto'
import { GeolocationResponse } from '@model/GeolocationResponse'
import { GetUpdateIntroducingBrokerTradingAccountRebateBulk } from '@model/GetUpdateIntroducingBrokerTradingAccountRebateBulk'
import { InitOrCreatePspToWalletDepositTransactionCommand } from '@model/InitOrCreatePspToWalletDepositTransactionCommand'
import { InitWalletToPspWithdrawTransactionCommand } from '@model/InitWalletToPspWithdrawTransactionCommand'
import { IntroducingBrokerCJMDto, IntroducingBrokerCJMParams } from '@model/IntroducingBrokerCJMDto'
import { IntroducingBrokerCampaignResultDto } from '@model/IntroducingBrokerCampaignResultDto'
import {
  IntroducingBrokerDetailsDto,
  IntroducingBrokerReferralDetailsDto,
} from '@model/IntroducingBrokerDetailsDto'
import {
  IBLoyaltyParams,
  LoyaltyDashboardDetailsDto,
  LoyaltyRewardsHistoryDto,
} from '@model/IntroducingBrokerLoyalty'
import {
  MetricsCountDto,
  MetricsDepositsDto,
  MetricsIBCommissionsDto,
  MetricsLotsDto,
  MetricsNVDto,
  MetricsNetDepositsDto,
} from '@model/IntroducingBrokerMetricsDto'
import {
  IBPerformanceParams,
  IntroducingBrokerPerformanceChartDetailDto,
} from '@model/IntroducingBrokerPerformance'
import { IntroducingBrokerRebateGroupDto } from '@model/IntroducingBrokerRebateGroupDto'
import { IntroducingBrokerReportDto } from '@model/IntroducingBrokerReportDto'
import { IntroducingBrokerRewardDto } from '@model/IntroducingBrokerRewardDto'
import { IntroducingBrokerValidateCode } from '@model/IntroducingBrokerValidateCode'
import { ClientLandingPageSettingDto, LandingPageDto } from '@model/LandingPageDto'
import { LeadDto } from '@model/LeadDto'
import { LeadResponseDto } from '@model/LeadResponseDto'
import { LeadVerificationByCodeValues } from '@model/LeadVerificationByCode'
import { LearningVideoDto } from '@model/LearningVideoDto'
import { LegalDocumentsWithTMCompany, OldLegalDocumentDto } from '@model/LegalDocumentDto'
import { LeverageTypeDto } from '@model/LeverageTypeDto'
import { LossPercentagesDto } from '@model/LossPercentagesDto'
import {
  LuckyDrawActivityType,
  LuckyDrawCampaign,
  LuckyDrawPrize,
  LuckyDrawResults,
  LuckyDrawRewardTypeResponse,
} from '@model/LuckyDrawDto'
import { MasterTransaction, normalizeMasterTransaction } from '@model/MasterTransaction'
import { MasterTransactionDto } from '@model/MasterTransactionDto'
import { MultiLevelCommissionDetailDto } from '@model/MultiLevelCommission'
import {
  AssignedCommissionPlan,
  IBNetworkPlans,
  SubIBAccount,
  SubIBAccountOption,
  UpdateCommissionPlan,
} from '@model/MultiTierDto'
import { NameDto } from '@model/NameDto'
import { Notification, NotificationPreferences } from '@model/Notification'
import { PaymentAgentDto } from '@model/PaymentAgentDto'
import { PayoutSchemeDto } from '@model/PayoutScheme'
import { PhoneNumberDto } from '@model/PhoneNumberDto'
import { PlatformInfoDto } from '@model/PlatformInfoDto'
import { PlatformType } from '@model/PlatformTypeDto'
import { PlatformTypeEnum } from '@model/PlatformTypeEnum'
import { PromoMaterialDto } from '@model/PromoMaterialDto'
import { LandingPagePromoOptionDto } from '@model/PromoOptionDto'
import { PublicDomainDto } from '@model/PublicDomainDto'
import {
  IBRebateBulkApprovalResponseDto,
  IBRebateSingleApprovalResponseDto,
  IntroducingBrokerRebateApprovalDto,
  RebateApprovalSummary,
} from '@model/RebateApprovalsDto'
import { RebateApprovalType, RebateGroupsSummaryDto } from '@model/RebateGroupsDto'
import { RebateTransactionDto, RebateTransactionsSummaryDto } from '@model/RebateTransactionDto'
import { ReferralLinkDto } from '@model/ReferralLinkDto'
import {
  ReferralMaterialDto,
  ReferralMaterialFilterOptionsDto,
  ReferralMaterialPreviewDto,
  ReferralMaterialPreviewUrls,
  ReferralMaterialsGenerateUrlParams,
  ReferralMaterialsUrls,
} from '@model/ReferralMaterialDto'
import {
  CreateReferralLinkAliasDto,
  CreateReferralLinkAliasDtoResponse,
  ReferralLinkAliasesDto,
  UpdateReferralLinkAliasDto,
} from '@model/ReferralUrlAliasDto'
import { RegulatorOptionDto } from '@model/RegulatorOptionDto'
import { RequiredDocument } from '@model/RequiredDocument'
import { RewardsHubCampaignsHistoryDto } from '@model/RewardsHubCampaignsHistoryDto'
import {
  RewardsHubDashboardDto,
  TradingPoolCampaignDetailDto,
  TradingPoolCampaignDetailsAboutModal,
  TradingPoolResultTypeDto,
} from '@model/RewardsHubDashboardDto'
import { RewardsHubRewardsHistoryDto } from '@model/RewardsHubRewardsHistoryDto'
import { StatementFile, StatementType } from '@model/Statements'
import { SubscriptionBundleDto } from '@model/SubscriptionBundleDto'
import { AddSubscriptionItemToCartData, SubscriptionCartItem } from '@model/SubscriptionCart'
import { SubscriptionGroupDto } from '@model/SubscriptionGroupDto'
import { SubscriptionHistoryItem, SubscriptionStateHistoryDto } from '@model/SubscriptionsDto'
import { SumsubAccessTokenData, SumsubApplicantState } from '@model/Sumsub'
import { SupportContactsDto } from '@model/SupportContactsDto'
import { TaxHistoryDto } from '@model/TaxHistoryDto'
import { IBTestSubCategory } from '@model/TestSectionsDto'
import { ThreeDSCreateSessionCommand } from '@model/ThreeDSCreateSessionCommand'
import { ThreeDSCreateSessionDto } from '@model/ThreeDSCreateSessionDto'
import { ThreeDSSessionDto } from '@model/ThreeDSSessionDto'
import { TickmillProductType } from '@model/TickmillProductType'
import { TotalWalletBalanceDto } from '@model/TotalWalletBalanceDto'
import {
  AddDemoTradingAccount,
  DemoTradingAccount,
  ResetTradingAccountPassword,
  TradingAccount,
  TradingAccountTotals,
  UpdateDemoTradingAccount,
  UpdateTradingAccountLeverage,
  UpdateTradingAccountNickname,
} from '@model/TradingAccount'
import { TradingAccountCampaignDto } from '@model/TradingAccountCampaignDto'
import { TradingAccountCreationOptions } from '@model/TradingAccountCreationOptions'
import { TradingAccountDetailDto } from '@model/TradingAccountDetailDto'
import { TradingAccountInfo } from '@model/TradingAccountInfo'
import { TradingAccountRebateDetailedDto } from '@model/TradingAccountRebateDetailedDto'
import { TradingAccountRebateDto } from '@model/TradingAccountRebateDto'
import { TradingAccountToWalletTransferDto } from '@model/TradingAccountToWalletTransferDto'
import { TradingPlaformsDataProps } from '@model/TradingPlatformsDto'
import { TransactionDto, TransactionStateHistoryDto } from '@model/TransactionDto'
import { TransactionLimitDto } from '@model/TransactionLimitDto'
import {
  ConfirmActionViaOTPData,
  GoogleAuthenticator2FAProviderDataDto,
  TwoFAProviderItem,
  TwoFactorAuthRecoveryCodesDto,
  Update2FAProvidersActionsData,
  Update2FAProvidersData,
  VerifyFirstTimeUseData,
} from '@model/TwoFactorAuthentication'
import { UpdateSubscriptionRequest } from '@model/UpdateSubscriptionRequest'
import { LoginResponse, TrustDeviceResponse, TwoFactor } from '@model/User'
import { ValidationOperation, ValidationOperationId } from '@model/ValidationDto'
import { VerifyAccountValues } from '@model/VerifyAccountValues'
import { VerifyTradingAccountPasswordCommand } from '@model/VerifyTradingAccountPassword'
import { WalletCreateDepositFromPaymentAgentTransaction } from '@model/WalletCreateDepositFromPaymentAgentTransaction'
import {
  CurrencyType,
  IsPhoneVerifiedForBonusCampaignDto,
  WalletCurrency,
  WalletDepositBonusCampaignDto,
  WalletDto,
  WalletType,
  WalletTypeEnum,
} from '@model/WalletDto'
import { WalletPaymentAgentTransferInformationDto } from '@model/WalletPaymentAgentTransferInformationDto'
import { WalletPaymentProvider, normalizePaymentProviders } from '@model/WalletPaymentProvider'
import { WalletPaymentProviderMethodResponseDto } from '@model/WalletPaymentProviderDto'
import {
  WalletPaymentProviderParameters,
  normalizeWalletPaymentProviderParameters,
} from '@model/WalletPaymentProviderParameters'
import { WalletPaymentProviderMethodParametersResponseDto } from '@model/WalletPaymentProviderParametersDto'
import { WalletToTradingAccountTransferDto } from '@model/WalletToTradingAccountTransferDto'
import { WalletToWalletTransferDto } from '@model/WalletToWalletTransferDto'
import { CreateQRParams } from '@model/createQR'

import { Locale } from '@global/locale/Locale'

import { ApiClient, PageData, PageQuery } from './ApiClient'
import { santizeBase64 } from './base64Helper'
import { TickmillCompaniesEnum } from './companyName.utils'
import { isEmpty } from './isEmpty'
import { preparePageQueryParams } from './queryParser'

export const emptyPaginationResponse = {
  items: [],
  itemsCount: 0,
  pageIndex: 0,
  pageSize: 0,
  totalPages: 0,
  totalResults: 0,
}

export class ClientApiClient {
  constructor(private apiClient: ApiClient) {}

  async login(values: LoginFormValues): Promise<LoginResponse> {
    return this.apiClient.post<LoginResponse>('/sign-in', values)
  }

  async logOut(userId: string): Promise<void> {
    return this.apiClient.post<void>('/sign-out', { userId })
  }

  async impersonatorLogin(
    clientId: string,
    tickmillPublicDomain: string,
    accessToken: string
  ): Promise<LoginResponse> {
    return this.apiClient.postWithAuthToken<LoginResponse>(
      '/impersonator',
      {
        clientId,
        tickmillPublicDomain,
      },
      accessToken
    )
  }

  async trustDevice2FA(values: { token: string; sessionId: string }): Promise<TrustDeviceResponse> {
    return this.apiClient.post('/2fa/trustdevice', values)
  }

  async verifyRecoveryCode(values: {
    email: string
    recoveryCode: string
  }): Promise<LoginResponse> {
    return this.apiClient.post('/2fa/recoverycodes/verify', values)
  }

  async resend2FACode(values: { typeId: number; token: string }): Promise<TwoFactor[]> {
    return this.apiClient.post(`/2fa/codes/resend`, values)
  }

  async getAccount(locale: Locale): Promise<AccountDetailedDto> {
    return this.apiClient.get<AccountDetailedDto>(`/account?languageId=${locale}`)
  }

  async getLossPercentages(): Promise<PageData<LossPercentagesDto>> {
    return this.apiClient.get<PageData<LossPercentagesDto>>(`/loss-percentages`)
  }

  async getVisitorGeolocation(): Promise<GeolocationResponse> {
    const resp = await this.apiClient.rawFetch('https://ipapi.co/json', {})
    return resp.data as GeolocationResponse
  }

  async getHBToken(): Promise<{ token: string }> {
    return this.apiClient.get('/generate-token')
  }

  async getAccountWithAppTestResults(locale: Locale): Promise<AccountDetailedDto> {
    const account = await this.getAccount(locale)
    return {
      ...account,
      isAppropriatenessTestAccountFailed: {
        [TickmillProductType.CFD]: !account.clientProductStates?.find(
          (x) => x.tickmillProduct.id === TickmillProductType.CFD
        )?.passedAppropriatenessTest,
        [TickmillProductType.ETD]: !account.clientProductStates?.find(
          (x) => x.tickmillProduct.id === TickmillProductType.ETD
        )?.passedAppropriatenessTest,
        [TickmillProductType.STOCKS]: !account.clientProductStates?.find(
          (x) => x.tickmillProduct.id === TickmillProductType.STOCKS
        )?.passedAppropriatenessTest,
        [TickmillProductType.SQF]: !account.clientProductStates?.find(
          (x) => x.tickmillProduct.id === TickmillProductType.SQF
        )?.passedAppropriatenessTest,
      },
    }
  }

  async resetPassword(values: ForgotPasswordFormValues): Promise<void> {
    return this.apiClient.post<void>('/password-reset/start', values)
  }

  async getResetPasswordAttempts(
    params: URLSearchParams
  ): Promise<{ attempts: number; attemptsLeft: number }> {
    return this.apiClient.get(`/password-reset/attempts?${new URLSearchParams(params)}`)
  }

  async resetPasswordComplete(
    values: Omit<ResetPasswordCompleteFormValues, 'confirmPassword'>
  ): Promise<{ id: string; email: string; twoFactor: TwoFactor[] | null }> {
    return this.apiClient.post('/password-reset/complete', values)
  }

  async createClient(data: ClientRegisterValues): Promise<any> {
    return this.apiClient.post('/client', data)
  }

  async autocompleteAddress(
    input: string,
    regionCodes: string[]
  ): Promise<AddressAutocompleteResponse> {
    const params = new URLSearchParams({ input })

    regionCodes.slice(0, 15).forEach((regionCode) => {
      params.append('regionCodes', regionCode)
    })

    return this.apiClient.get(`/account/autocomplete-address?${params.toString()}`)
  }

  async getPlaceAddressDetails(placeId: string): Promise<PlaceAddressDetails> {
    return this.apiClient.get(`/account/place/${encodeURIComponent(placeId)}`)
  }

  async getCountries(params?: {
    displayAllCountries?: boolean
    showHiddenCountries?: boolean
  }): Promise<CountryPreferenceStrippedDto[]> {
    let url = '/countries/preferences'
    const queryParams = []
    if (!isEmpty(params?.displayAllCountries)) {
      queryParams.push(`displayAllCountries=${params?.displayAllCountries}`)
    }
    if (!isEmpty(params?.showHiddenCountries)) {
      queryParams.push(`showHiddenCountries=${params?.showHiddenCountries}`)
    }

    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`
    }

    return this.apiClient.get(url)
  }

  async getCompanyTypes(locale: string): Promise<NameDto[]> {
    return this.apiClient.get(`/company-types?languageId=${locale}`)
  }

  async getLegalBases(locale: string): Promise<NameDto[]> {
    return this.apiClient.get(`/legalbases?languageId=${locale}`)
  }

  async getWallets(query?: PageQuery): Promise<WalletDto[]> {
    return this.apiClient.get(`/wallets?${preparePageQueryParams(query)}`)
  }

  async getTransactionLimits(): Promise<TransactionLimitDto[]> {
    return this.apiClient.get<TransactionLimitDto[]>(`/wallets/limits/min`)
  }

  async addWallet(data: CreateWalletDto): Promise<WalletType[]> {
    return this.apiClient.post<WalletType[]>(`/wallets`, data)
  }

  async getWalletById(walletId: string): Promise<WalletDto> {
    return this.apiClient.get<WalletDto>(`/wallets/${walletId}`)
  }

  async getTradingAccounts(query?: PageQuery): Promise<PageData<TradingAccount>> {
    return this.apiClient.get<PageData<TradingAccount>>(
      `/trading-accounts?${preparePageQueryParams(query)}`
    )
  }

  async getTradingAccountsTotals(): Promise<TradingAccountTotals> {
    return this.apiClient.get(`/trading-accounts/totals`)
  }

  async getDemoTradingAccountsTotals(): Promise<TradingAccountTotals> {
    return this.apiClient.get(`/trading-accounts/totals/demo`)
  }

  async getLiveTradingAccountsTotals(): Promise<TradingAccountTotals> {
    return this.apiClient.get(`/trading-accounts/totals/live`)
  }

  async getInactiveTradingAccountsTotals(): Promise<TradingAccountTotals> {
    return this.apiClient.get(`/trading-accounts/totals/inactive`)
  }

  async getTradingAccountById(tradingAccountId: string): Promise<TradingAccountDetailDto> {
    return this.apiClient.get<TradingAccountDetailDto>(`/trading-accounts/${tradingAccountId}`)
  }

  async getTATradeSSOToken(tradingAccountId: string): Promise<{ token: string; url: string }> {
    return this.apiClient.get<{ token: string; url: string }>(
      `/trading-accounts/${tradingAccountId}/trade-sso-token`
    )
  }

  async addTradingAccount(values: CreateTradingAccount): Promise<void> {
    return this.apiClient.post<void>(`/trading-accounts`, values)
  }

  async addCampaignTradingAccount(values: CreateCampaignTradingAccount): Promise<void> {
    return this.apiClient.post<void>(`/trading-accounts/create-for-campaign`, values)
  }

  async addDemoTradingAccount(values: CreateDemoTradingAccount): Promise<AddDemoTradingAccount> {
    return this.apiClient.post<AddDemoTradingAccount>(`/demo-trading-accounts`, values)
  }

  async getClientEntityConfiguration(): Promise<EntityConfigurationDto> {
    return this.apiClient.get(`/trading-accounts/entity-configuration`)
  }

  async getTradingAccountsConfiguration(): Promise<ClientAccountsConfigurationDto> {
    return this.apiClient.get(`/trading-accounts/accounts-configuration`)
  }

  async getDemoTradingAccounts(query?: PageQuery): Promise<PageData<DemoTradingAccount>> {
    return this.apiClient.get(`/demo-trading-accounts?${preparePageQueryParams(query)}`)
  }

  async addBalanceToDemoTradingAccount(
    values: DemoTradingAccountDepositFormValues
  ): Promise<PageData<DemoTradingAccount>> {
    return this.apiClient.post(`/demo-trading-accounts/deposit`, {
      tradingAccountId: values.transferTo.id,
      amount: values.amount,
    })
  }

  async getPlatformTypes(
    tickmillProductId: TickmillProductType,
    options: { ibCode?: string } = {}
  ): Promise<PlatformType[]> {
    const params = new URLSearchParams()

    if (options.ibCode) params.set('IBCode', options.ibCode)

    const qs = params.toString() ? `?${params.toString()}` : ''

    return this.apiClient.get(`/trading-accounts/products/${tickmillProductId}/platform-types${qs}`)
  }

  // DXtrade
  async getDxtradeOpenPositions(
    accountCode: string,
    query?: PageQuery
  ): Promise<DxTradeOpenPositionsResponse> {
    const searchParams = preparePageQueryParams(query)
    // Remove default pagination if present – endpoint returns full open positions list
    searchParams.delete('pageSize')
    searchParams.delete('pageIndex')
    const qs = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return this.apiClient.get<DxTradeOpenPositionsResponse>(
      `/platform/accounts/${accountCode}/positions${qs}`
    )
  }

  async getDxtradeAccountMetrics(
    accountCode: string,
    includePositions?: boolean
  ): Promise<DxTradeAccountMetricsResponse> {
    const qs = includePositions ? '?include-positions=true' : ''
    return this.apiClient.get<DxTradeAccountMetricsResponse>(
      `/platform/accounts/${accountCode}/metrics${qs}`
    )
  }

  async getDxtradeOrders(accountCode: string): Promise<DxTradeOrdersResponse> {
    return this.apiClient.get<DxTradeOrdersResponse>(`/platform/accounts/${accountCode}/orders`)
  }

  async createDxtradeOrder(
    accountCode: string,
    order: DxTradeSingleOrderRequest
  ): Promise<DxTradeOrderCreateResponse> {
    return this.apiClient.post<DxTradeOrderCreateResponse>(
      `/platform/accounts/${accountCode}/orders`,
      order
    )
  }

  async getDxtradeOrdersHistory(
    accountCode: string,
    query?: DxtradeOrdersHistoryQuery
  ): Promise<DxTradeOrdersResponse> {
    const params = prepareDxtradeOrdersHistoryParams(query)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return this.apiClient.get<DxTradeOrdersResponse>(
      `/platform/accounts/${accountCode}/orders/history${qs}`
    )
  }

  async getDxtradeCashTransfers(
    accountCode: string,
    query?: DxtradeCashTransfersQuery
  ): Promise<DxTradeAccountTransfersResponse> {
    const params = prepareDxtradeCashTransfersParams(query)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return this.apiClient.get<DxTradeAccountTransfersResponse>(
      `/platform/accounts/${accountCode}/transfers${qs}`
    )
  }

  async getDxtradeInstruments(query?: DxtradeInstrumentQuery): Promise<DxTradeInstrumentDetails[]> {
    const params = prepareDxtradeInstrumentQueryParams(query)

    const qs = params.toString() ? `?${params.toString()}` : ''
    const response = await this.apiClient.get<DxTradeInstrumentResponse>(
      `/platform/instruments/query${qs}`
    )
    return response.instruments ?? []
  }

  async getDxtradeWatchlists(query?: DxtradeWatchlistsQuery): Promise<WatchlistModel[]> {
    const params = new URLSearchParams()
    if (query?.login && query?.domain) {
      params.set('login', query.login)
      params.set('domain', query.domain)
    }

    const qs = params.toString() ? `?${params.toString()}` : ''
    return this.apiClient.get<WatchlistModel[]>(`/platform/watchlists${qs}`)
  }

  async createWatchlist(payload: CreateWatchlistModel): Promise<WatchlistModel> {
    return this.apiClient.post<WatchlistModel>('/platform/watchlists', payload)
  }

  async updateWatchlist(id: number, payload: UpdateWatchlistModel): Promise<WatchlistModel> {
    return this.apiClient.put<WatchlistModel>(`/platform/watchlists/${id}`, payload)
  }

  async deleteWatchlist(id: number): Promise<void> {
    return this.apiClient.delete(`/platform/watchlists/${id}`)
  }

  async bulkUpdateWatchlistOrderValues(payload: BulkUpdateWatchlistOrderValues): Promise<void> {
    return this.apiClient.patch('/platform/watchlists/order-values', payload)
  }

  async getDxtradeAccountInstruments(
    accountCode: string,
    query?: DxtradeInstrumentQuery
  ): Promise<DxTradeInstrumentDetailsResponseItem[]> {
    if (!accountCode) return []

    const params = prepareDxtradeInstrumentQueryParams(query)
    const qs = params.toString() ? `?${params.toString()}` : ''

    const response = await this.apiClient.get<DxTradeInstrumentDetailsResponse>(
      `/platform/accounts/${accountCode}/instruments/query${qs}`
    )

    return response.instrumentDetails ?? []
  }

  async getDxtradeInstrumentDetails(
    accountCode: string,
    symbol: string
  ): Promise<DxTradeInstrumentDetailsResponseItem[] | undefined> {
    if (!accountCode || !symbol) return undefined
    const encodedSymbol = encodeURIComponent(symbol)
    const response = await this.apiClient.get<DxTradeInstrumentDetailsResponse>(
      `/platform/accounts/${accountCode}/instruments/${encodedSymbol}`
    )
    return response.instrumentDetails ?? []
  }

  async getDxtradeConversionRates(
    fromCurrency: string,
    toCurrency: string
  ): Promise<DxTradeConversionRatesApiResponse> {
    const params = new URLSearchParams({
      fromCurrency,
      toCurrency,
    })

    return this.apiClient.get<DxTradeConversionRatesApiResponse>(
      `/platform/conversionRates?${params.toString()}`
    )
  }

  async getMarketDataEvents(payload: MarketDataRequest): Promise<MarketDataList> {
    return this.apiClient.post<MarketDataList>(`/platform/marketdata`, payload)
  }

  async getDynamicLeverage(): Promise<AssetClassMarginRequirementDto[]> {
    return this.apiClient.get<AssetClassMarginRequirementDto[]>(`/platform/dynamic-leverage/all`)
  }

  async getDxtradeLatestCandles(
    symbols: string[],
    candleTypeId: string
  ): Promise<DxTradeRawCandle[]> {
    if (!symbols.length || !candleTypeId) return []

    const params = new URLSearchParams()
    symbols.forEach((s, i) => params.append(`symbols[${i}]`, s))
    params.set('candleTypeId', candleTypeId)

    const candles = await this.apiClient.get<DxTradeRawCandle[]>(
      `/platform-marketdata/candles/latest?${params.toString()}`
    )

    return candles ?? []
  }

  async getDxtradeCandles(
    symbols: string[],
    candleTypeId: string,
    from: number,
    to: number
  ): Promise<DxTradeRawCandle[]> {
    const candles = await this.apiClient.post<DxTradeRawCandle[]>('/platform-marketdata/candles', {
      from: new Date(from * 1000).toISOString(),
      to: new Date(to * 1000).toISOString(),
      candleTypeId,
      symbols,
    })

    return candles ?? []
  }

  async getDxtradePublicTradingInfo(params: {
    accountCurrency?: string
    instrument?: string
    lotSize?: number
    leverage?: number
    classification?: string
    tickmillCompany?: string
    openPrice?: number
    closePrice?: number
    direction?: number
    platformType?: string
  }): Promise<DxTradeTradingInfoDto> {
    const searchParams = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.set(key, String(value))
      }
    })

    // TODO: change when endpoint will work on localhost and dev (now it is uvailable only for prod)

    const response = await fetch(
      `https://www.tickmill.com/api/calculate/prices?${searchParams.toString()}`,
      {
        method: 'GET',
        credentials: 'omit',
        headers: {
          Accept: 'application/json',
        },
      }
    )

    return (await response.json()) as DxTradeTradingInfoDto
  }

  async getCurrencies(platformTypeId?: PlatformTypeEnum): Promise<WalletCurrency[]> {
    return this.apiClient.get<WalletCurrency[]>(
      `/trading-accounts/platform-types/${platformTypeId}/currencies`
    )
  }

  async getWalletCurrencies(walletTypeId: number): Promise<WalletCurrency[]> {
    const response = await this.apiClient.get<{ allCurrencies: WalletCurrency[] }>(
      `/wallets/wallet-currencies?WalletTypeId=${walletTypeId}`
    )
    return response.allCurrencies
  }

  async getDemoCurrencies(props: {
    platformTypeId: number
    tickmillCompanyId: number
  }): Promise<WalletCurrency[]> {
    const params = {
      platformTypeId: props.platformTypeId.toString(),
      tickmillCompanyId: props.tickmillCompanyId.toString(),
    }
    return this.apiClient.get<WalletCurrency[]>(
      `/demo-trading-accounts/currencies?${new URLSearchParams(params)}`
    )
  }

  async updateDisplayCurrency(currencyId: string): Promise<void> {
    return this.apiClient.put(`/me/display-currency/${currencyId}`, {})
  }

  async addTickmillProduct(productId: TickmillProductType): Promise<void> {
    return this.apiClient.put(`/me/product/${productId}`, {})
  }

  async getIntroducingBrokerCurrenciesByPlatformId(
    introducingBrokerId: string,
    platformTypeId: number
  ): Promise<WalletCurrency[]> {
    return this.apiClient.get<WalletCurrency[]>(
      `/introducing-brokers/${introducingBrokerId}/platform-types/${platformTypeId}/currencies`
    )
  }

  async getIBReports(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<PageData<IntroducingBrokerReportDto>> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/reports/clients?${preparePageQueryParams(query)}`
    )
  }

  async validateAccountIntroducingBrokerByCode(
    introducingBrokerCode: string
  ): Promise<IntroducingBrokerValidateCode> {
    return this.apiClient.get<IntroducingBrokerValidateCode>(
      `/account/introducing-brokers/valid/${introducingBrokerCode}`
    )
  }

  async getIntroducingBrokerSummary(): Promise<BrokerSummaryDto> {
    return this.apiClient.get<BrokerSummaryDto>(`/introducing-broker/summary/incoming-reserved`)
  }

  async getIntroducingBrokerTransactionsSummary(
    walletId: string
  ): Promise<IntroducingBrokerRewardDto> {
    return this.apiClient.get<IntroducingBrokerRewardDto>(
      `/introducing-broker/wallets/${walletId}/ib-all-time-rewards`
    )
  }

  async getRewardPrograms(
    dateTime: string,
    tickmillCompanyId?: number,
    country?: string
  ): Promise<PageData<RewardProgramDto>> {
    return this.apiClient.get(
      `/reward-programs?search=(StartDate <= ${dateTime}) AND (EndDate >= ${dateTime}) AND (TickmillCompany == ${tickmillCompanyId}) AND (Country == ${country})`
    )
  }

  async getRewardProgramDetails(id: string, locale: Locale): Promise<RewardProgramDetailsDto> {
    return this.apiClient.get(`/reward-programs/${id}?languageId=${locale}`)
  }

  async getRewardProgramEligibilityCheck(
    id: string,
    locale: Locale
  ): Promise<RewardProgramEligibilityCheckDto> {
    return this.apiClient.get(`/reward-programs/${id}/check-eligibility?languageId=${locale}`)
  }

  async postClaimReward(rewardTierId: string, data: any): Promise<void> {
    return this.apiClient.post(`/reward-programs/${rewardTierId}/claim`, data)
  }

  async getIBCommissionPlansSummary(introducingBrokerId: string): Promise<AssignedCommissionPlan> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/commission-plan-groups/assigned`
    )
  }

  async getIBCommissionPlans(introducingBrokerId: string): Promise<AssignedCommissionPlan[]> {
    return this.apiClient.get(`/introducing-brokers/${introducingBrokerId}/commission-plan-groups`)
  }

  async addCommissionPlan(
    introducingBrokerId: string,
    commissionPlans: UpdateCommissionPlan[]
  ): Promise<void> {
    return this.apiClient.post(
      `/introducing-brokers/${introducingBrokerId}/commission-plan-groups`,
      {
        CommissionPlans: commissionPlans,
      }
    )
  }

  async updateCommissionPlan(
    introducingBrokerId: string,
    commissionPlanId: string,
    commissionPlans: UpdateCommissionPlan[]
  ): Promise<void> {
    return this.apiClient.put(
      `/introducing-brokers/${introducingBrokerId}/commission-plan-groups/${commissionPlanId}`,
      {
        CommissionPlans: commissionPlans,
      }
    )
  }

  async getIBSubAccounts(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<PageData<SubIBAccount>> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/multi-tier-network/sub-accounts?${preparePageQueryParams(
        query
      )}`
    )
  }

  async updateIBSubAccount(
    introducingBrokerId: string,
    subAccountId: number,
    commissionPlanGroupId: string
  ): Promise<void> {
    return this.apiClient.put(
      `/introducing-brokers/${introducingBrokerId}/multi-tier-network/sub-accounts/${subAccountId}`,
      {
        CommissionPlanGroupId: commissionPlanGroupId,
      }
    )
  }

  async getIBNetworkData(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<PageData<IBNetworkPlans>> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/multi-tier-network?${preparePageQueryParams(
        query
      )}`
    )
  }

  async getIBSubAccountPlanOptions(introducingBrokerId: string): Promise<SubIBAccountOption[]> {
    return this.apiClient.get(`/introducing-brokers/${introducingBrokerId}/commission-plan-groups`)
  }

  async updateTradingAccountLeverage(
    tradingAccountId: string,
    data: UpdateTradingAccountLeverage
  ): Promise<void> {
    return this.apiClient.put<void>(`/trading-accounts/${tradingAccountId}/leverage`, data)
  }

  async updateTradingAccountNickname(data: UpdateTradingAccountNickname): Promise<void> {
    return this.apiClient.put<void>(`/trading-accounts/update-nickname`, data)
  }

  async updateDemoTradingAccount(
    tradingAccountId: string,
    data: UpdateDemoTradingAccount
  ): Promise<void> {
    return this.apiClient.put<void>(`/demo-trading-accounts/${tradingAccountId}`, data)
  }

  async updateDemoTradingAccountNickname(data: UpdateTradingAccountNickname): Promise<void> {
    return this.apiClient.put<void>(`/demo-trading-accounts/update-nickname`, data)
  }

  async getClientCampaigns(locale = 'en'): Promise<ClientCampaignDto[]> {
    return this.apiClient.get(`/campaigns?languageId=${locale}`)
  }

  async getClientCampaignsList(): Promise<PageData<ClientCampaignDto>> {
    return this.apiClient.get(`/campaigns/list`)
  }

  async getTradingPoolCampaignResults(
    campaignId: string,
    query?: PageQuery
  ): Promise<PageData<TradingPoolResultTypeDto>> {
    return this.apiClient.get(
      `trading-pool-campaigns/${campaignId}/results?${preparePageQueryParams(query)}`
    )
  }

  async getTradingPoolCampaign(
    campaignId: string,
    locale = 'en'
  ): Promise<TradingPoolCampaignDetailDto> {
    return this.apiClient.get(`trading-pool-campaigns/${campaignId}?languageId=${locale}`)
  }
  async getTradingPoolCampaignDetailsAboutModal(
    campaignId: string,
    locale: string
  ): Promise<TradingPoolCampaignDetailsAboutModal> {
    return this.apiClient.get(`/trading-pool-campaigns/${campaignId}/about?languageId=${locale}`)
  }

  async getClientAppTests(locale: string, productId?: TickmillProductType): Promise<ClientTestDto> {
    if (productId) {
      return this.apiClient.get(`/account/ap-test?languageId=${locale}&productId=${productId}`)
    } else return this.apiClient.get(`/account/ap-test?languageId=${locale}`)
  }

  async getEconomicProfileTest(
    locale: string,
    productId?: TickmillProductType
  ): Promise<ClientTestDto> {
    return this.apiClient.get(
      `/account/economic-profile-test-questions?languageId=${locale}&&productId=${productId}`
    )
  }

  async getEMIRTestQuestions(locale: string): Promise<ClientTestDto> {
    return this.apiClient.get(`/account/corporate_test?SubcategoryId=9&languageId=${locale}`)
  }

  async createClientTests(values: CreateClientTest): Promise<void> {
    return this.apiClient.post(`/account/tests`, values)
  }

  async getRegulators(
    tickmillCompanyId: number,
    languageId: string,
    country?: string | undefined | null
  ): Promise<RegulatorOptionDto[]> {
    const params = Object.fromEntries(
      Object.entries({
        Fcountry: country || '',
        languageId,
      }).filter(([, v]) => !!v)
    )
    const searchParams = new URLSearchParams(params).toString()

    return this.apiClient.get(`/regulators/${tickmillCompanyId}?${searchParams}`)
  }

  async getLeverageTypes(platformTypeId?: number): Promise<LeverageTypeDto[]> {
    if (!platformTypeId) {
      return this.apiClient.get<LeverageTypeDto[]>(`/trading-accounts/leverage-types`)
    }
    return this.apiClient.get<LeverageTypeDto[]>(
      `/trading-accounts/leverage-types-for-platform/${platformTypeId}`
    )
  }

  async getAccountType(
    platformTypeId: number,
    currency: string,
    accountGroupTypeId = AccountGroupType.Live,
    options: { ibCode?: string } = {}
  ): Promise<NameDto[]> {
    const params = new URLSearchParams()

    if (options.ibCode) params.set('IBCode', options.ibCode)

    const qs = params.toString() ? `?${params.toString()}` : ''

    return this.apiClient.get<NameDto[]>(
      `/trading-accounts/platform-types/${platformTypeId}/currencies/${currency}/account-group-types/${accountGroupTypeId}/account-types${qs}`
    )
  }

  async getIBReferralCodes(
    introducingBrokerId: string
  ): Promise<IntroducingBrokerReferralDetailsDto[]> {
    return this.apiClient.get(`/introducing-brokers/${introducingBrokerId}/referral-codes`)
  }

  async getTradingAccountsAccountInfo(): Promise<TradingAccountInfo[]> {
    return this.apiClient.get<TradingAccountInfo[]>(`/trading-accounts/accounts-info`)
  }

  async getAvailableWallets(walletTypeId: number = WalletTypeEnum.CFD): Promise<WalletCurrency[]> {
    // 1 means FX/CFD
    return this.apiClient.get<WalletCurrency[]>(
      `/wallets/types/${walletTypeId}/currencies/available`
    )
  }

  async getWalletTypes(): Promise<WalletType[]> {
    return this.apiClient.get<WalletType[]>(`/wallets/types`)
  }

  getTransactions: (query?: PageQuery) => Promise<PageData<TransactionDto>> = (query) => {
    return this.apiClient.get(`/transactions?${preparePageQueryParams(query)}`)
  }

  getCorporateActions: (query?: PageQuery) => Promise<PageData<CorporateActionDto>> = (query) => {
    return this.apiClient.get(`/corporate-actions?${preparePageQueryParams(query)}`)
  }

  async getTransactionStates(locale: string): Promise<NameDto[]> {
    return this.apiClient.get(`/transactions/states?languageId=${locale}`)
  }

  async getTransactionStatusHistory(
    transactionId: string,
    languageId: string
  ): Promise<TransactionStateHistoryDto> {
    return this.apiClient.get(
      `/transactions/${transactionId}/transaction-state-history?languageId=${languageId}`
    )
  }

  async getSubscriptionStatusHistory(
    id: string,
    locale: string
  ): Promise<SubscriptionStateHistoryDto[]> {
    return this.apiClient.get(`/subscriptions/requests/${id}/state-history?languageId=${locale}`)
  }

  async changeTradingAccountPassword(values: ChangeTradingAccountPasswordValues): Promise<void> {
    return this.apiClient.put(`trading-accounts/update-password`, values)
  }

  async resetTradingAccountPassword(values: ResetTradingAccountPassword): Promise<void> {
    return this.apiClient.put('/trading-accounts/reset-password', values)
  }

  async resetDemoTradingAccountPassword(values: ResetTradingAccountPassword): Promise<void> {
    return this.apiClient.put('/demo-trading-accounts/reset-password', values)
  }

  async transactionDeposit(
    data: Omit<InitOrCreatePspToWalletDepositTransactionCommand, 'caller' | 'clientIpAddress'>
  ): Promise<MasterTransaction> {
    const referrerUrl = import.meta.env.DEV
      ? 'https://dev-bo01-ca-02-my.d.tickmill.com'
      : window.location.origin
    const referrerUrlDetail = [{ name: 'referrer_url', value: referrerUrl }]

    const response = await this.apiClient.post<MasterTransactionDto>('/transactions/deposit', {
      ...data,
      // TODO: need to send this data through headers, and backend should catch the data
      caller: 'ca',
      details: data.details.concat(referrerUrlDetail),
      referrer_url: referrerUrl,
    })

    return normalizeMasterTransaction(response)
  }

  async transactionWithdraw(
    data: Omit<InitWalletToPspWithdrawTransactionCommand, 'caller' | 'clientIpAddress'>
  ): Promise<MasterTransaction> {
    // TODO: backend should improve the "endpoint" "language logic"
    const referrerUrl = import.meta.env.DEV
      ? 'https://dev-bo01-ca-02-my.d.tickmill.com'
      : window.location.origin
    const referrerUrlDetail = [{ name: 'referrer_url', value: referrerUrl }]

    const response = await this.apiClient.post<MasterTransactionDto>('/transactions/withdraw', {
      ...data,
      // TODO: need to send this data through headers, and backend should catch the data
      caller: 'ca',
      // TODO: backend should get the client ip.
      clientIpAddress: '127.0.0.1',
      details: data.details.concat(referrerUrlDetail),
      referrer_url: referrerUrl,
    })

    return normalizeMasterTransaction(response)
  }

  async cancelTransaction(transactionId: string): Promise<void> {
    return this.apiClient.post('/transactions/cancel', {
      transactionId,
      transactionStateReasonId: 610,
      caller: 'ca',
      clientIpAddress: '127.0.0.1',
    })
  }

  async cancelWithdrawal(transactionId: string): Promise<void> {
    return this.apiClient.post('/transactions/cancel-withdrawal', {
      transactionId,
    })
  }

  async getWalletDepositMethods(
    locale: string,
    walletId: string
  ): Promise<WalletPaymentProvider[]> {
    // TODO: backend should improve the "endpoint" "language logic"
    // TODO: front-end should store "language" in localstorage
    const response = await this.apiClient.get<WalletPaymentProviderMethodResponseDto>(
      `/wallets/${walletId}/languages/${locale}/deposit-methods`
    )
    return normalizePaymentProviders(response)
  }

  async getWalletDepositMethodParameters(
    locale: string,
    walletId: string,
    methodId: string
  ): Promise<WalletPaymentProviderParameters> {
    // TODO: backend should improve the "endpoint" "language logic"
    const response = await this.apiClient.get<WalletPaymentProviderMethodParametersResponseDto>(
      `/wallets/${walletId}/languages/${locale}/deposit-methods/${methodId}/parameters`
    )
    return normalizeWalletPaymentProviderParameters(response)
  }

  async getEnabledCardDetails(): Promise<CardDetailsDto[]> {
    return this.apiClient.get<CardDetailsDto[]>(`/card-details/enabled-by-client`)
  }

  async getBasisTheoryCardTokenId(cardTokenId: string): Promise<CardTokenIdResponseDto> {
    return this.apiClient.get<CardTokenIdResponseDto>(`/tokens/cards/${cardTokenId}/token-id`)
  }

  async createThreeDSSession(data: ThreeDSCreateSessionCommand): Promise<ThreeDSCreateSessionDto> {
    return this.apiClient.post<ThreeDSCreateSessionDto>('/3ds/create-session', data)
  }

  async getThreeDSSession(sessionId: string): Promise<ThreeDSSessionDto> {
    return this.apiClient.get<ThreeDSSessionDto>(`/3ds/session/${sessionId}`)
  }
  async getWalletDepositBonusCampaigns(
    walletId: string,
    paymentMethodId: string,
    depositAmount: number
  ): Promise<WalletDepositBonusCampaignDto> {
    return this.apiClient.get<WalletDepositBonusCampaignDto>(
      `/wallets/${walletId}/payment-methods/${paymentMethodId}/available-campaigns?DepositAmount=${depositAmount}`
    )
  }

  async getIsPhoneVerified(): Promise<IsPhoneVerifiedForBonusCampaignDto> {
    return this.apiClient.get<IsPhoneVerifiedForBonusCampaignDto>('/is-phone-number-verified')
  }

  async getWalletWithdrawalMethods(
    locale: string,
    walletId: string
  ): Promise<WalletPaymentProvider[]> {
    // TODO: backend should improve the "endpoint" "language logic"
    const response = await this.apiClient.get<WalletPaymentProviderMethodResponseDto>(
      `/wallets/${walletId}/languages/${locale}/withdrawal-methods`
    )
    return normalizePaymentProviders(response)
  }

  async getWalletWithdrawalMethodParameters(
    locale: string,
    walletId: string,
    methodId: string
  ): Promise<WalletPaymentProviderParameters> {
    // TODO: backend should improve the "endpoint" "language logic"
    const response = await this.apiClient.get<WalletPaymentProviderMethodParametersResponseDto>(
      `/wallets/${walletId}/languages/${locale}/withdrawal-methods/${methodId}/parameters`
    )
    return normalizeWalletPaymentProviderParameters(response)
  }

  async currencyConverterFromTo(
    fromCurrency: string,
    toCurrency: string
  ): Promise<ExchangeRateResponseDto> {
    return this.apiClient.get<ExchangeRateResponseDto>(
      `/exchange-rates/latest?BaseCcy=${fromCurrency}&QuoteCcy=${toCurrency}&direction=sel`
    )
  }

  async getTotalBalanceByCurrency(
    currencyId: CurrencyType,
    walletType: WalletTypeEnum = WalletTypeEnum.ETD
  ): Promise<TotalWalletBalanceDto> {
    return this.apiClient.get<TotalWalletBalanceDto>(
      `/wallets/total-balance/${currencyId}?search=WalletTypeId == ${walletType}`
    )
  }

  async getTotalBalanceByCurrencyBothProducts(
    currencyId: CurrencyType
  ): Promise<TotalWalletBalanceDto> {
    return this.apiClient.get<TotalWalletBalanceDto>(`/wallets/total-balance/${currencyId}`)
  }

  async getExchangeRate(query?: PageQuery): Promise<ExchangeRateResponseDto> {
    return this.apiClient.get<ExchangeRateResponseDto>(
      `/exchange-rates/latest?${preparePageQueryParams(query)}`
    )
  }

  async convertTradingAccountBalance(
    values: CreateTradingAccountBalanceConversionTransaction
  ): Promise<MasterTransactionDto> {
    return this.apiClient.post(`/transactions/trading-account-balance-conversion`, values)
  }

  async transferWalletToWallet(values: WalletToWalletTransferDto): Promise<MasterTransactionDto> {
    return this.apiClient.post(`/transactions/transfer`, values)
  }

  async transferWalletToTradingAccount(
    values: WalletToTradingAccountTransferDto
  ): Promise<MasterTransactionDto> {
    return this.apiClient.post(`/transactions/transfer-to-trading-account`, values)
  }

  async transferTradingAccountToWallet(
    values: TradingAccountToWalletTransferDto
  ): Promise<MasterTransactionDto> {
    return this.apiClient.post(`/transactions/transfer-from-trading-account`, values)
  }

  async getActiveBonus(walletId: string): Promise<PageData<BonusTransactionDto>> {
    return this.apiClient.get(`/transactions/${walletId}/active-bonus-with-required-lots`)
  }

  async getPaymentAgentsTransferInformation(
    walletId: string,
    paymentAgentId: string
  ): Promise<WalletPaymentAgentTransferInformationDto> {
    return this.apiClient.get(
      `/wallets/${walletId}/payment-agents/${paymentAgentId}/transfer-information`
    )
  }

  async paymentAgentAcceptTerms(
    paymentAgentId: string
  ): Promise<WalletPaymentAgentTransferInformationDto> {
    return this.apiClient.post(`/payment-agents/${paymentAgentId}/accept-terms`, {})
  }

  async getIntroducingBrokerById(
    locale: string,
    id: string | undefined
  ): Promise<IntroducingBrokerDetailsDto> {
    return this.apiClient.get(`/introducing-brokers/${id}?languageId=${locale}`)
  }

  async consumeLoyaltyReward(rewardId: string): Promise<void> {
    return this.apiClient.post(`/consume-loyalty-reward`, { rewardId })
  }

  async getIntroducingBrokerLegalDocuments(
    locale: string,
    legalDocumentsSchemes: string
  ): Promise<OldLegalDocumentDto[]> {
    if (!legalDocumentsSchemes) {
      return []
    }
    return this.apiClient.get(
      `/introducing-broker/legal-documents?languageId=${locale}${legalDocumentsSchemes}`
    )
  }

  async getLegalDocuments(locale: string): Promise<CompanyLegalDocumentDto[]> {
    return await this.apiClient.get(`/new-legal-documents?languageId=${locale}`)
  }

  async getLegalDocumentsQuery(
    locale: string,
    tickmillCompanyId?: number,
    tickmillProductIds: number[] = [],
    countryId?: string
  ): Promise<LegalDocumentDto[]> {
    const productLegalDocuments: CompanyLegalDocumentDto[] = await this.apiClient.get(
      `/new-legal-documents?languageId=${locale}`
    )

    if (tickmillCompanyId && tickmillProductIds?.length >= 1 && countryId) {
      const [productCountryLegalDocument] = productLegalDocuments.filter(
        (x) => x.tickmillCompanyId === tickmillCompanyId
      )
      const productCountryLegalDocuments =
        productCountryLegalDocument?.productCountryLegalDocuments || []

      return productCountryLegalDocuments
        .filter((x) => tickmillProductIds.includes(x.tickmillProductId))
        .reduce((previousValue: LegalDocumentDto[], currentValue) => {
          return previousValue.concat(currentValue.legalDocuments)
        }, [])
        .filter((x) => {
          if (!x.showForAllCountries && !x.requiredForAllCountries) {
            return !!x.availableCountries.find((x) => x.country.id === countryId)
          }

          if (!x.showForAllCountries && x.requiredForAllCountries) {
            return !!x.availableCountries.find((x) => x.country.id === countryId)
          }

          return x
        })
    }

    if (tickmillCompanyId && tickmillProductIds?.length >= 1) {
      const [productCountryLegalDocument] = productLegalDocuments.filter(
        (x) => x.tickmillCompanyId === tickmillCompanyId
      )
      const productCountryLegalDocuments =
        productCountryLegalDocument?.productCountryLegalDocuments || []

      return productCountryLegalDocuments
        .filter((x) => tickmillProductIds.includes(x.tickmillProductId))
        .reduce((previousValue: LegalDocumentDto[], currentValue) => {
          return previousValue.concat(currentValue.legalDocuments)
        }, [])
    }

    if (tickmillCompanyId) {
      const [productCountryLegalDocument] = productLegalDocuments.filter(
        (x) => x.tickmillCompanyId === tickmillCompanyId
      )
      const productCountryLegalDocuments =
        productCountryLegalDocument?.productCountryLegalDocuments || []

      return productCountryLegalDocuments.reduce(
        (previousValue: LegalDocumentDto[], currentValue) => {
          return previousValue.concat(currentValue.legalDocuments)
        },
        []
      )
    }

    return []
  }

  async getLeadByIdAndToken(leadId: string, token: string): Promise<LeadDto> {
    return this.apiClient.get(`/leads/by-token/${leadId}/${token}`)
  }

  async getNationalities(): Promise<NameDto<CountryCode>[]> {
    return this.apiClient.get('/nationalities')
  }

  async getTaxUnavailableReasons(locale: string): Promise<NameDto[]> {
    return this.apiClient.get(`/tin-unavailable-reasons?languageId=${locale}`)
  }

  async verifyEmail(values: VerifyAccountValues): Promise<void> {
    return this.apiClient.put('/leads/verify', values)
  }

  async continueLeadRegistration(values: ContinueRegistrationValues): Promise<void> {
    return this.apiClient.put('/leads/continue-registration', values)
  }

  async verifyLeadUsingCode(values: LeadVerificationByCodeValues): Promise<LeadDto> {
    return this.apiClient.put<LeadDto>('/leads/verify-code', values)
  }

  async validateEmailDomain(email: string): Promise<boolean> {
    return this.apiClient.get(`/restricted-email-domains/${email}/validate`)
  }

  async getResetPasswordValidationRules(query?: PageQuery): Promise<ValidationOperation> {
    return this.apiClient.get(
      `/password-reset/validation-rules/${
        ValidationOperationId.ValidateClientPlainPassword
      }?${preparePageQueryParams(query)}`
    )
  }

  async getLeadPasswordValidationRules(
    leadData: {
      id: string
      token: string
    },
    locale: string
  ): Promise<ValidationOperation> {
    return this.apiClient.get(
      `/validation-rules/${ValidationOperationId.ValidateClientPlainPassword}?languageId=${locale}&LeadId=${leadData.id}&Token=${leadData.token}`
    )
  }

  async getPasswordValidationRules(
    validationOperationId: string,
    locale: string
  ): Promise<ValidationOperation> {
    return this.apiClient.get(`/validation-rules/${validationOperationId}?languageId=${locale}`)
  }

  async cancelLeadPhoneNumberVerification(
    leadId: string,
    verificationId: string
  ): Promise<{ id: string }> {
    return this.apiClient.put(
      `/lead/${leadId}/cancel-phone-number-verification/${verificationId}`,
      {
        leadId,
        verificationId,
      }
    )
  }

  async createPhoneNumberVerification(
    leadId: string,
    values: CreatePhoneNumberVerification
  ): Promise<{ leadId: string; phoneVerificationId: string }> {
    return this.apiClient.post(`/lead/${leadId}/create-phone-number-verification`, values)
  }

  async updateLeadPhoneNumber(
    leadId: string,
    phoneNumber: string,
    countryCode: string
  ): Promise<LeadDto> {
    return this.apiClient.put(`/lead/${leadId}/phone-number`, {
      PhoneNumber: phoneNumber,
      CountryCode: countryCode,
    })
  }

  async sendNewVerificationCodeToPhoneNumber(
    leadId: string,
    verificationId: string,
    values: RestartPhoneNumberVerification
  ): Promise<{ leadId: string; phoneVerificationId: string }> {
    return this.apiClient.put(`/lead/${leadId}/verifications/${verificationId}/send-new-code`, {
      leadId,
      verificationId,
      ...values,
    })
  }

  async getReferralLink(
    locale: string,
    domain: string,
    generateShortenUrl = false,
    referralCode?: string
  ): Promise<ReferralLinkDto> {
    if (referralCode) {
      return this.apiClient.get(
        `/promo-materials/referral-url?languageId=${locale}&domain=${domain}&introducingBrokerCode=${referralCode}&generateShortenUrl=${generateShortenUrl}`
      )
    }
    return this.apiClient.get(
      `/promo-materials/referral-url?languageId=${locale}&domain=${domain}&generateShortenUrl=${generateShortenUrl}`
    )
  }

  async getReferralLandingPageLink(
    locale: string,
    landingPageId: string,
    domain: string,
    referralCode?: string,
    generateShortenUrl?: boolean
  ): Promise<ReferralLinkDto> {
    if (referralCode) {
      return this.apiClient.get(
        `/promo-materials/referral-url?languageId=${locale}&landingPageId=${landingPageId}&domain=${domain}&introducingBrokerCode=${referralCode}&generateShortenUrl=${generateShortenUrl}`
      )
    }
    return this.apiClient.get(
      `/promo-materials/referral-url?languageId=${locale}&landingPageId=${landingPageId}&domain=${domain}`
    )
  }

  async getReferralMaterials(
    locale: Locale,
    query?: PageQuery
  ): Promise<PageData<ReferralMaterialDto>> {
    const languageId = overrideKoToKrLanguage(locale)
    return this.apiClient.get(
      `/referral-materials?${preparePageQueryParams(query)}&languageId=${languageId}`
    )
  }

  async getReferralMaterialsUrls(): Promise<ReferralMaterialsUrls[]> {
    return this.apiClient.get(`/referral-materials/categories/urls`)
  }

  async getReferralMaterialsPreviewUrls(): Promise<ReferralMaterialPreviewUrls[]> {
    return this.apiClient.get(`/referral-materials/categories/urls/clientarea`)
  }

  async getReferralUrlByCategory(data: {
    ibCode: string
    generateShortenUrl?: boolean
    isCustomUrl: boolean
    urlId: string
    domain: string
  }): Promise<{ url: string; shortenUrl: string }> {
    const { ibCode, generateShortenUrl = false, isCustomUrl, urlId, domain } = data
    const urlSearchParam = isCustomUrl ? 'customUrl' : 'url'

    const params = new URLSearchParams({
      [urlSearchParam]: urlId,
      introducingBrokerCode: ibCode,
      generateShortenUrl: String(generateShortenUrl),
      domain,
    })

    return this.apiClient.get(`/referral-materials/categories/urls/referral-url?${params}`)
  }

  async getQRCode(data: {
    domain: string
    isCustomUrl: boolean
    urlId: string
    ibCode: string
    generateShortenUrl?: boolean
  }): Promise<File> {
    const { domain, isCustomUrl, urlId, ibCode, generateShortenUrl = false } = data
    const urlSearchParam = isCustomUrl ? 'customUrl' : 'url'

    const params = new URLSearchParams({
      domain,
      [urlSearchParam]: urlId,
      introducingBrokerCode: ibCode,
      resolution: '240x240',
      shortenUrl: String(generateShortenUrl),
      imageFormat: 'jpeg',
    })

    const response = await this.apiClient.fetch<File>(
      `/referral-materials/categories/urls/qr-code?${params.toString()}`,
      {
        method: 'GET',
        responseType: 'blob',
      }
    )

    return response.data
  }

  async getLanguages(params?: { clientAreaVisible?: boolean }): Promise<ClientAreaLanguageDto[]> {
    let url = '/languages'
    if (!isEmpty(params?.clientAreaVisible)) {
      url += `?clientAreaVisible=${params?.clientAreaVisible}`
    }

    return this.apiClient.get(url)
  }

  async getClientAreaLanguages({
    promoMaterialCategoryId,
    forSignIn,
    clientAreaVisible,
  }: {
    promoMaterialCategoryId?: number
    forSignIn?: boolean
    clientAreaVisible?: boolean
  }): Promise<ClientAreaLanguageDto[]> {
    if (forSignIn) {
      return this.getLanguages({ clientAreaVisible })
    }
    return this.apiClient.get(`/landing-pages/languages/${promoMaterialCategoryId}`)
  }

  async getClientDateFormats(): Promise<NameDto<number>[]> {
    return this.apiClient.get('/date-format-types')
  }

  async getLandingPages({
    promoMaterialCategoryId,
    query,
  }: {
    promoMaterialCategoryId?: number
    query?: PageQuery
  }): Promise<PageData<LandingPageDto>> {
    return this.apiClient.get(
      `/landing-pages/${promoMaterialCategoryId}?${preparePageQueryParams(query)}`
    )
  }

  async getPromoMaterials(id: string, query?: PageQuery): Promise<PageData<PromoMaterialDto>> {
    return this.apiClient.get(
      `/landing-pages/${id}/promo-materials?${preparePageQueryParams(query)}`
    )
  }

  async getPromoOptionsByLandingPageAndCategory(
    landingPageId: string,
    categoryId: number
  ): Promise<LandingPagePromoOptionDto> {
    return this.apiClient.get(`/landing-pages/${landingPageId}/category/${categoryId}/options`)
  }

  async getIntroducingBrokerIncomeSummary(query?: PageQuery): Promise<BrokerSummaryDto> {
    return this.apiClient.get(
      `/introducing-broker/overall-summary?${preparePageQueryParams(query)}`
    )
  }

  async getIntroducingBrokerIncomeSummaryPerAccount(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<PageData<AccountSummaryDto>> {
    return this.apiClient.get(
      `/introducing-broker/${introducingBrokerId}/summary-per-trading-account?${preparePageQueryParams(
        query
      )}`
    )
  }

  async getIBClientFilterOptions(introducingBrokerId: string): Promise<FilterOptionsDto> {
    return this.apiClient.get(
      `/introducing-broker/${introducingBrokerId}/summary-per-trading-account/filter-options`
    )
  }

  async getIBIncomeFilterOptions(): Promise<FilterOptionsDto> {
    return this.apiClient.get('/introducing-broker/income/filter-options')
  }

  async getIBReportsFilterOptions(introducingBrokerId: string): Promise<FilterOptionsDto> {
    return this.apiClient.get<{ [x: string]: string }>(
      `/introducing-brokers/${introducingBrokerId}/reports/clients/filter-options`
    )
  }

  async getClientJourneyMapData(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<PageData<ClientJourneyMapDto>> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/reports/clients/client-journey-map?${preparePageQueryParams(
        query
      )}`
    )
  }

  async getClientJourneyMapSummary(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<ClientJourneyMapSummaryDto> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/reports/clients/client-journey-map/summary?${preparePageQueryParams(
        query
      )}`
    )
  }

  async getTransactionHistoryFilterOptions(
    clientWalletType: string,
    languageId: string
  ): Promise<FilterOptionsDto> {
    return this.apiClient.get(
      `/transactions/filter-options?ca_search_ClientWalletType=${clientWalletType}&languageId=${languageId}`
    )
  }

  async getPATransactionHistoryFilterOptions(
    clientId: string,
    walletId: string
  ): Promise<FilterOptionsDto> {
    return this.apiClient.get(
      `/transactions/filter-options?ca_search_Client=${clientId}&ca_search_WalletId=${walletId}`
    )
  }

  async getIntroducingBrokerIncome(query?: PageQuery): Promise<PageData<DealDto>> {
    return this.apiClient.get(`/introducing-broker/income?${preparePageQueryParams(query)}`)
  }

  async getPaymentAgent(paymentAgentId: string): Promise<PaymentAgentDto> {
    return this.apiClient.get(`/payment-agents/${paymentAgentId}`)
  }

  getCampaigns: (query?: PageQuery) => Promise<PageData<ClientRebateCampaignDto>> = (query) => {
    return this.apiClient.get(`/campaigns/list?${preparePageQueryParams(query)}`)
  }

  getTradingAccountRebateCampaignById: (
    tradingAccountId: string,
    campaignId: string,
    locale: string
  ) => Promise<ClientRebateCampaignDetailDto> = (tradingAccountId, campaignId, locale) => {
    return this.apiClient.get(
      `/trading-accounts/${tradingAccountId}/client-rebate-campaigns/${campaignId}?languageId=${locale}`
    )
  }

  getRebateCampaignResults: (
    tradingAccountId: string,
    campaignId: string,
    query?: PageQuery
  ) => Promise<PageData<ClientCampaignRebateResultDto>> = (
    tradingAccountId,
    campaignId,
    query?: PageQuery
  ) => {
    return this.apiClient.get(
      `/trading-accounts/${tradingAccountId}/client-rebate-campaigns/${campaignId}/results?${preparePageQueryParams(
        query
      )}`
    )
  }

  getTradingAccountContestCampaignById: (
    tradingAccountId: string,
    campaignId: string,
    locale: string
  ) => Promise<ClientContestCampaignDetailDto> = (tradingAccountId, campaignId, locale) => {
    return this.apiClient.get(
      `/trading-accounts/${tradingAccountId}/client-contest-campaigns/${campaignId}?languageId=${locale}`
    )
  }
  getTradingAccountContestCampaignDays: (
    tradingAccountId: string,
    campaignId: string
  ) => Promise<TradingDaysPerCampaignDto | void> = (tradingAccountId, campaignId) => {
    return this.apiClient.get(
      `trading-accounts/${tradingAccountId}/client-contest-campaigns/${campaignId}/active-trading-days`
    )
  }
  getTradingAccountContestCampaignDrawdown: (
    tradingAccountId: string,
    campaignId: string
  ) => Promise<DrawdownDto | void> = (tradingAccountId, campaignId) => {
    return this.apiClient.get(
      `trading-accounts/${tradingAccountId}/client-contest-campaigns/${campaignId}/drawdown`
    )
  }

  getContestCampaignResults: (
    tradingAccountId: string,
    campaignId: string,
    query?: PageQuery
  ) => Promise<PageData<ClientContestCampaignResultDto>> = (
    tradingAccountId,
    campaignId,
    query?: PageQuery
  ) => {
    return this.apiClient.get(
      `/trading-accounts/${tradingAccountId}/client-contest-campaigns/${campaignId}/results?${preparePageQueryParams(
        query
      )}`
    )
  }

  getCampaignByIdResults: (
    campaignId: string,
    query?: PageQuery
  ) => Promise<PageData<ClientCampaignRebateResultDto>> = (campaignId, query?: PageQuery) => {
    return this.apiClient.get(`/campaigns/${campaignId}/results?${preparePageQueryParams(query)}`)
  }

  getIbContestCampaigns: (query?: PageQuery) => Promise<PageData<ClientIbContestCampaignDto>> = (
    query
  ) => {
    return this.apiClient.get(`/ib-campaigns/list?${preparePageQueryParams(query)}`)
  }

  getIbContestByIdCampaign: (campaignId: string) => Promise<ClientIbContestCampaignDetailDto> = (
    campaignId
  ) => {
    return this.apiClient.get(`/ib-campaigns/${campaignId}`)
  }

  getIbContestByIdCampaignResults: (
    campaignId: string,
    query?: PageQuery
  ) => Promise<PageData<ClientIbContestCampaignResultDto>> = (campaignId, query) => {
    return this.apiClient.get(
      `/ib-campaigns/${campaignId}/results?${preparePageQueryParams(query)}`
    )
  }

  async getLuckyDrawPrizes(campaignId: string, locale: Locale): Promise<LuckyDrawPrize[]> {
    return this.apiClient.get(`/campaigns/${campaignId}/lucky-draw-rewards?languageId=${locale}`)
  }

  async getLuckyDrawRewardTypes(locale: string): Promise<LuckyDrawRewardTypeResponse[]> {
    return this.apiClient.get(`/campaigns/lucky-draw-reward-types?languageId=${locale}`)
  }

  async getLuckyDrawResults(campaignId: string, locale: string): Promise<LuckyDrawResults> {
    return this.apiClient.get(`/campaigns/${campaignId}/lucky-draw-results?languageId=${locale}`)
  }

  async getLuckyDrawActivityTypes(locale: string): Promise<LuckyDrawActivityType[]> {
    return this.apiClient.get(`/campaigns/lucky-draw-activity-types?languageId=${locale}`)
  }

  async getLuckyDrawChances(campaignId: string): Promise<number> {
    return this.apiClient.get(`/campaigns/${campaignId}/lucky-draw-chances-count`)
  }

  async getLuckDrawPrize(campaignId: string): Promise<LuckyDrawPrize> {
    return this.apiClient.get(`/campaigns/${campaignId}/lucky-draw-chances-consume-next`)
  }

  async getLuckyDrawCampaign(locale: string): Promise<LuckyDrawCampaign[]> {
    return this.apiClient.get(`/campaigns/lucky-draw?languageId=${locale}`)
  }

  async getIBSignUpTest(locale: string, subCategoryId: IBTestSubCategory): Promise<ClientTestDto> {
    return this.apiClient.get(`/ib-signup-test?languageId=${locale}&subCategoryId=${subCategoryId}`)
  }

  async signUpIntroducingBroker(values: CreateClientTest): Promise<void> {
    return await this.apiClient.post('/account/tests', values)
  }

  async paymentAgentValidateClientDetails(
    paymentAgentWalletId: string,
    clientWalletName: string
  ): Promise<WalletDto> {
    return this.apiClient.get(
      `/payment-agents/validate-client-details?paymentAgentWalletId=${paymentAgentWalletId}&clientWalletName=${clientWalletName}`
    )
  }

  async tradingAccountValidateCampaignAccount(
    walletId: string,
    campaignName: string
  ): Promise<TradingAccountCampaignDto> {
    return this.apiClient.get(
      `/validate-campaign-account?walletId=${walletId}&tradingAccountLogin=${campaignName}`
    )
  }

  async transactionDepositFromPaymentAgent(
    locale: string,
    data: Omit<WalletCreateDepositFromPaymentAgentTransaction, 'caller' | 'clientIpAddress'>
  ): Promise<WalletCreateDepositFromPaymentAgentTransaction> {
    // TODO: backend should improve the "endpoint" "language logic"

    return this.apiClient.post(`/transactions/deposit-from-payment-agent?languageId=${locale}`, {
      ...data,
      base64Content: santizeBase64(data.base64Content),
      // TODO: need to send this data through headers, and backend should catch the data
      caller: 'ca',
      // TODO: backend should get the client ip.
      clientIpAddress: '127.0.0.1',
    })
  }

  async createLead(values: CreateLead): Promise<LeadResponseDto> {
    return this.apiClient.post(`/leads`, values)
  }

  async resendCreateLeadEmail(id: string): Promise<void> {
    return this.apiClient.put(`/leads/${id}/resend-email`, {})
  }

  async resendCreateLeadEmailByEmail(email: string): Promise<void> {
    return this.apiClient.put(`/leads/${email}/resend-email-by-email`, {})
  }

  async getLandingPageOptions(locale: string): Promise<ClientLandingPageSettingDto[]> {
    return this.apiClient.get(`/client-area/landing-pages/options?languageId=${locale}`)
  }

  async updateLandingPage(landingPageId: number): Promise<void> {
    return this.apiClient.put(`/me/preferred-landing-page/${landingPageId}`, {})
  }

  async updatePassword(
    values: ChangePasswordFormValues
  ): Promise<{ email: string; id: string; twoFactor: TwoFactor[] | null }> {
    return this.apiClient.put('/me/password', values)
  }

  async updateNewsletter(newsletterChoice: boolean): Promise<void> {
    return this.apiClient.put<void>(`/me/newsletter/${newsletterChoice}`, {})
  }

  async updateCommunicationLanguage(
    id: string,
    communicationLanguage: CommunicationLanguageFormValues
  ): Promise<void> {
    return this.apiClient.put<void>(
      `/me/communication-language/${communicationLanguage.communicationLanguageId}`,
      {}
    )
  }

  async updateClientDateFormat(id: string, values: DateFormatFormValues): Promise<void> {
    return this.apiClient.put<void>(`/me/date-format`, {
      id,
      dateFormatTypeId: values.dateFormatTypeId,
    })
  }

  async updateMarketDataClassifications(marketDataClassificationId: number): Promise<void> {
    return this.apiClient.put<void>(`/me/market-data-classification`, {
      marketDataClassificationId,
    })
  }

  async getDocuments(query?: PageQuery): Promise<PageData<DocumentClientAreaDto>> {
    return this.apiClient.get(`/documents?${preparePageQueryParams(query)}`)
  }

  async getAccountNciPriorities(): Promise<ClientNciPriorityDto[]> {
    return this.apiClient.get('/account/nci/priorities')
  }

  async getAccountNciAvailablePriorities(locale: string): Promise<ClientNciPriorityDto> {
    return this.apiClient.get(`/account/nci/available-priorities?languageId=${locale}`)
  }

  async accountAcceptNciHistory(data: ClientCreateNciHistory): Promise<void> {
    return this.apiClient.post('/account/nci/history', data)
  }

  async accountAcceptFinalNciHistory(data: ClientCreateNciFinalHistory): Promise<void> {
    return this.apiClient.post('/account/nci/history', data)
  }

  async uploadDocuments(data: CreateClientDocuments): Promise<DocumentDto[]> {
    const cleanedDocs = data.documents.filter((x) => !!x?.filename)
    const uploadPromises = cleanedDocs.map((document) =>
      this.apiClient.post('/documents', {
        documents: [
          {
            ...document,
            file: document.file
              ?.replace('data:image/png;base64,', '')
              ?.replace('data:image/jpeg;base64,', '')
              ?.replace('data:application/pdf;base64,', '')
              ?.replace(
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                ''
              ),
          },
        ],
      })
    )
    const uploadedDocs = await Promise.all(uploadPromises)
    return uploadedDocs.flat() as DocumentDto[]
  }

  async deleteDocument(documentId: string): Promise<void> {
    return this.apiClient.delete(`/documents/${documentId}`)
  }

  async getDocumentCategories(
    documentCategoryId: number,
    locale: string
  ): Promise<DocumentCategoryDto> {
    return this.apiClient.get(
      `/documents/categories/${documentCategoryId}/types?languageId=${locale}`
    )
  }

  async getAllDocumentCategories(locale: string): Promise<DocumentCategorySizeDto[]> {
    return this.apiClient.get(`/documents/categories?languageId=${locale}`)
  }

  async validateIntroducingBrokerByCode(
    introducingBrokerCode: string,
    tickmillCompanyId?: TickmillCompaniesEnum
  ): Promise<{ id: string; name: string }> {
    let url = `introducing-brokers/valid/${introducingBrokerCode}`
    if (tickmillCompanyId) {
      url = url += `/${tickmillCompanyId}`
    }
    return this.apiClient.get(url)
  }

  async validatePhoneNumber(number: string, countryCode: string): Promise<PhoneNumberDto> {
    return this.apiClient.get(
      `/phone-number-lookup?PhoneNumber=${encodeURIComponent(countryCode)}${number}`
    )
  }

  async validateBasePhoneNumber(number: string, countryCode: string): Promise<PhoneNumberDto> {
    return this.apiClient.get(
      `/phone-number-base-lookup?PhoneNumber=${encodeURIComponent(countryCode)}${number}`
    )
  }

  async validatePhoneCode(
    leadId: string,
    verificationId: string,
    code: string
  ): Promise<PhoneNumberDto> {
    return this.apiClient.put(
      `/lead/${leadId}/complete-phone-number-verification/${verificationId}`,
      { code }
    )
  }

  async getClientClassificationTestAnswers(query?: PageQuery): Promise<TestAnswersDto> {
    return this.apiClient.get(`/account/classification-answers?${preparePageQueryParams(query)}`)
  }

  async getClassificationTest(query?: PageQuery): Promise<ClientTestDto> {
    return this.apiClient.get(`/account/classification-test?${preparePageQueryParams(query)}`)
  }

  async getAccountLegalDocuments(locale: string): Promise<LegalDocumentsWithTMCompany> {
    return this.apiClient.get(`/account/legal-documents?languageId=${locale}`)
  }

  async addClientClassificationTest(values: CreateClientTest): Promise<AxiosResponse> {
    return await this.apiClient.fetch('/account/tests', {
      method: 'POST',
      data: values,
    })
  }

  async getPlatformInfo(query: PageQuery): Promise<PlatformInfoDto[]> {
    return await this.apiClient.get(`/platforms-info?${preparePageQueryParams(query)}`)
  }

  async getAccountKycTest(locale: string): Promise<ClientTestDto> {
    return await this.apiClient.get(`/account/kyc-test?languageId=${locale}`)
  }

  async updateAccountKycDetailsResponse(values: CreateKycUpdateTicket): Promise<AxiosResponse> {
    return await this.apiClient.fetch('/account/kyc-update-details', {
      method: 'POST',
      data: values,
    })
  }

  async updateAccountDormantDetailsResponse(
    values: CreateDormantUpdateTicket
  ): Promise<AxiosResponse> {
    return await this.apiClient.fetch(`/account/dormant-update-details`, {
      method: 'POST',
      data: values,
    })
  }

  async getTaxFormQuestions(query: PageQuery): Promise<ClientTestDto> {
    return await this.apiClient.get(`/account/tax-form-test?${preparePageQueryParams(query)}`)
  }

  async getTaxFormAnswers(query: PageQuery): Promise<TestAnswersDto> {
    return await this.apiClient.get(`/account/tax-form-answers?${preparePageQueryParams(query)}`)
  }

  async getAccountAppTestAnswers(
    locale: Locale,
    tickmillProduct: TickmillProductType = TickmillProductType.CFD
  ): Promise<TestAnswersDto> {
    return await this.apiClient.get(
      `/account/ap-test-answers?languageId=${locale}&tickmillProduct=${tickmillProduct}`
    )
  }

  async getAccountAppTest(locale: string): Promise<ClientTestDto> {
    return await this.apiClient.get(`/account/ap-test?languageId=${locale}`)
  }

  async getEconomicProfileTestAnswers(locale: Locale): Promise<TestAnswersDto> {
    return await this.apiClient.get(`/account/ep-test-answers?languageId=${locale}`)
  }

  async acceptAppTestRisk(payload: {
    clientId: string
    clientTestId: string
    riskAccepted: boolean
  }): Promise<void> {
    return await this.apiClient.put(`/account/accept-apptest-risk`, payload)
  }

  async getApTestAttempts(productId?: TickmillProductType): Promise<number> {
    return await this.apiClient.get(
      `/account/ap-test-attempts${productId ? `?productId=${productId}` : ''}`
    )
  }

  async getIBCampaignsResults(): Promise<PageData<IntroducingBrokerCampaignResultDto>> {
    return await this.apiClient.get('/ib-campaigns/list')
  }

  async getRequiredDocuments(): Promise<RequiredDocument[]> {
    return await this.apiClient.get(`/required-documents`)
  }

  async getTaxHistory(locale: string): Promise<TaxHistoryDto[]> {
    return this.apiClient.get(`/account/tax-form-history?languageId=${locale}`)
  }

  async getOwnershipStructures(locale: string): Promise<NameDto[]> {
    return this.apiClient.get(`/ownership-structures?languageId=${locale}`)
  }

  async addPhoneNumber(values: PhoneNumberAddValues): Promise<ClientPhoneNumberDto> {
    return this.apiClient.post('/client/phone-numbers', values)
  }

  async updatePhoneNumber(phoneNumberId: string, values: PhoneNumberAddValues): Promise<void> {
    return this.apiClient.put(`/client/phone-numbers/${phoneNumberId}`, values)
  }

  async resendPhoneNumberVerification(
    verificationId: string,
    phoneNumberId: string,
    workflow: PhoneVerificationWorkflowEnum
  ): Promise<{ phoneVerificationId: string }> {
    return this.apiClient.put(`/client/verifications/${verificationId}/send-new-code`, {
      workflow,
      phoneNumberId,
      verificationId,
    })
  }

  async validateClientPhoneNumber(phoneNumber: string): Promise<void> {
    return this.apiClient.get(
      `/client/phone-number-lookup?phoneNumber=${encodeURIComponent(phoneNumber)}`
    )
  }

  async deletePhoneNumber(phoneNumberId: string): Promise<void> {
    return this.apiClient.delete(`/client/phone-numbers/${phoneNumberId}`)
  }

  async requestPhoneNumberVerification(phoneNumberId: string): Promise<{
    clientId: string
    phoneVerificationId: string
  }> {
    return this.apiClient.post(`/client/phone-numbers/${phoneNumberId}/verifications`, {})
  }

  async verifyPhoneNumber(
    phoneNumberId: string,
    verificationId: string,
    code: string
  ): Promise<void> {
    return this.apiClient.put(
      `/client/phone-numbers/${phoneNumberId}/verifications/${verificationId}`,
      { code }
    )
  }

  async cancelPhoneNumberVerification(
    phoneNumberId: string,
    verificationId: string
  ): Promise<void> {
    return this.apiClient.put(
      `/client/phone-numbers/${phoneNumberId}/verifications/${verificationId}/cancel`,
      {}
    )
  }

  async getTradingAccountClassificationType(
    platformTypeId: number,
    currencyId: CurrencyType,
    accountGroupTypeId: AccountGroupType,
    accountTypeId: number
  ): Promise<NameDto[]> {
    return this.apiClient.get(
      `/trading-accounts/platform-types/${platformTypeId}/currencies/${currencyId}/account-group-types/${accountGroupTypeId}/account-types/${accountTypeId}/classification-types`
    )
  }

  async getNotifications(query?: PageQuery): Promise<PageData<Notification>> {
    return await this.apiClient.get(`/notifications?${preparePageQueryParams(query)}`)
  }

  async getNotification(
    id: string,
    query: PageQuery,
    clientNotificationId?: string
  ): Promise<Notification> {
    const queryParams = preparePageQueryParams(query)
    if (clientNotificationId) {
      queryParams.append('ClientNotificationId', clientNotificationId)
    }
    return this.apiClient.get(`/notifications/${id}?${queryParams}`)
  }

  async readAllNotifications(
    notificationClientNotificationPairList: {
      notificationId: string
      clientNotificationId: string | null
    }[]
  ): Promise<void> {
    return await this.apiClient.put(`/notifications/mark-all-as-read`, {
      notificationClientNotificationPairList,
    })
  }

  async getNotificationPreferences(): Promise<NotificationPreferences> {
    return await this.apiClient.get('/clients/preferences')
  }

  async getTruliooCountries(
    countryId: string,
    languageId: string,
    leadId: string
  ): Promise<TruliooEIdData[]> {
    return await this.apiClient.get(
      `/countries/registration-fields?countryId=${countryId}&languageId=${languageId}&leadId=${leadId}`
    )
  }

  async updateNotificationPreferences(values: NotificationPreferences): Promise<void> {
    return await this.apiClient.put('/notifications/clients/preferences', values)
  }

  async deleteNotification(
    notificationId: string,
    clientNotificationId: string | undefined
  ): Promise<void> {
    return await this.apiClient.put(`/notifications/mark-as-deleted/${notificationId}`, {
      clientNotificationId,
    })
  }

  async readNotification(
    notificationId: string,
    clientNotificationId: string | undefined
  ): Promise<void> {
    return await this.apiClient.put(`/notifications/mark-as-read/${notificationId}`, {
      clientNotificationId,
    })
  }

  async getSupportContacts(companyId: number): Promise<SupportContactsDto> {
    return await this.apiClient.get(`/support-contacts/${companyId}`)
  }

  async getEducationalVideos(languageId: string): Promise<LearningVideoDto[]> {
    return this.apiClient.get(
      `/educational-videos?productId=${TickmillProductType.ETD}&languageId=${languageId}`
    )
  }

  async getTutorialVideos(languageId: string): Promise<LearningVideoDto[]> {
    return this.apiClient.get(
      `/tutorial-videos?productId=${TickmillProductType.ETD}&languageId=${languageId}`
    )
  }

  async getSubscriptionBundles(): Promise<SubscriptionBundleDto[]> {
    return this.apiClient.get('subscriptions/subscription-bundles')
  }

  async getActiveSubscriptions(
    query: PageQuery
  ): Promise<{ items: SubscriptionHistoryItem[]; itemsCount: number }> {
    return this.apiClient.get(`/subscriptions/requests?${preparePageQueryParams(query)}`)
  }

  async getSubscriptionGroups(): Promise<SubscriptionGroupDto[]> {
    return this.apiClient.get('subscriptions/subscription-groups')
  }

  async updateSubscriptionRequest(data: UpdateSubscriptionRequest): Promise<void> {
    return this.apiClient.post('/subscriptions/requests', data)
  }

  async getSubscriptionsHistory(query: PageQuery): Promise<PageData<SubscriptionHistoryItem>> {
    return this.apiClient.get(`/subscriptions/requests?${preparePageQueryParams(query)}`)
  }

  async getBankAccounts(): Promise<BankAccount[]> {
    return this.apiClient.get('/bank-account-details')
  }

  async addBankAccount(values: BankAccountCreate): Promise<BankAccount> {
    return this.apiClient.post('/bank-account-details', values)
  }

  async getBankAccountValidationOptions(): Promise<BankAccountValidationOptions> {
    return this.apiClient.get('/bank-account-details/validation-options')
  }

  async getGuidesTypes(): Promise<NameDto[]> {
    return this.apiClient.get(`/guides/types`)
  }

  async getGuideByTypeId(guideTypeId: number, query?: PageQuery): Promise<ClientGuideDto> {
    return this.apiClient.get(`/client/guide/${guideTypeId}?${preparePageQueryParams(query)}`)
  }

  async updateClientGuideRead(guideTypeId: number, query?: PageQuery): Promise<void> {
    return this.apiClient.put(
      `/client/guide/${guideTypeId}/read?${preparePageQueryParams(query)}`,
      {}
    )
  }

  async updateClientGuideDismiss(guideTypeId: number, query?: PageQuery): Promise<void> {
    return this.apiClient.put(
      `/client/guide/${guideTypeId}/dismiss?${preparePageQueryParams(query)}`,
      {}
    )
  }

  async getClientProductSubscriptions(
    type: string,
    languageId: string
  ): Promise<TradingPlaformsDataProps[]> {
    return this.apiClient.get(
      `/subscriptions/subscription-platforms/${type}?languageId=${languageId}`
    )
  }

  async getClientProductsByToken(leadId: string, token: string): Promise<NameDto[]> {
    return this.apiClient.get(`/products/by-token/${leadId}/${token}`)
  }

  async getTradingAccountStatement(
    tradingAccountId: string,
    StatementType: StatementType,
    query: PageQuery
  ): Promise<StatementFile> {
    return this.apiClient.get(
      `/statements/${tradingAccountId}/${StatementType}?${preparePageQueryParams(query).toString()}`
    )
  }

  async checkTradingAccountStatement(
    tradingAccountId: string,
    StatementType: StatementType,
    query: PageQuery
  ): Promise<{ dates: string[] }> {
    return this.apiClient.get(
      `/statements/${tradingAccountId}/${StatementType}/dates/?${preparePageQueryParams(
        query
      ).toString()}`
    )
  }

  async getSubscriptionCartItems(): Promise<SubscriptionCartItem[]> {
    return this.apiClient.get('subscriptions/cart/items')
  }

  async getSubscriptionCartItem(id: string): Promise<SubscriptionCartItem> {
    return this.apiClient.get(`subscriptions/cart/items/${id}`)
  }

  async deleteSubscriptionCartItem(id: string): Promise<void> {
    return this.apiClient.delete(`subscriptions/cart/items/${id}`)
  }

  async addSubscriptionItemToCart(values: AddSubscriptionItemToCartData): Promise<void> {
    return this.apiClient.post('subscriptions/cart', values)
  }

  async getDisplayCurrencies(): Promise<WalletCurrency[]> {
    return this.apiClient.get<WalletCurrency[]>('/display-currencies')
  }

  async get2FAProviderTypes(): Promise<NameDto<number>[]> {
    return this.apiClient.get<NameDto<number>[]>('2fa/providers/types')
  }

  async verifyDuplicatePassword(values: VerifyTradingAccountPasswordCommand): Promise<void> {
    return this.apiClient.post('/trading-accounts/verify-duplicate-password', values)
  }

  async get2FAActionConfirmationOtp(
    values: ConfirmActionViaOTPData,
    locale: string
  ): Promise<{
    data: TwoFactor
  }> {
    return this.apiClient.post(`/action-confirmation/otp?languageId=${locale}`, values)
  }

  async get2FAProviderActionTypes(): Promise<NameDto[]> {
    return this.apiClient.get('2fa/auth-types')
  }

  async enable2FAProvidersActionType(value: Update2FAProvidersActionsData): Promise<void> {
    return this.apiClient.post('2fa/auth-actions', value)
  }

  async disable2FAProvidersActionType(
    payload: Update2FAProvidersActionsData
  ): Promise<{ email: string; id: string; twoFactor: TwoFactor[] }> {
    return this.apiClient.delete('2fa/auth-actions', payload)
  }

  async enable2FAProviders(value: Update2FAProvidersData): Promise<{
    data: TwoFactor
  }> {
    return this.apiClient.post('2fa/providers', value)
  }

  async disable2FAProviders(
    payload: Update2FAProvidersData
  ): Promise<{ email: string; id: string; twoFactor: TwoFactor[] }> {
    return this.apiClient.delete(`2fa/providers`, payload)
  }

  async enableGoogleAuthenticator2FAProviders(
    value: Update2FAProvidersData
  ): Promise<GoogleAuthenticator2FAProviderDataDto> {
    return this.apiClient.post('2fa/providers', value)
  }

  async verify2FACodesFirstTimeUse(
    value: VerifyFirstTimeUseData
  ): Promise<{ data: { email: string } }> {
    return this.apiClient.post('2fa/codes/verify-first-time-use', value)
  }

  async get2FAProviders(): Promise<TwoFAProviderItem[]> {
    return this.apiClient.get<TwoFAProviderItem[]>('2fa/providers')
  }

  async get2FARecoveryCodesGenerate(): Promise<TwoFactorAuthRecoveryCodesDto> {
    return this.apiClient.get(`/2fa/recoverycodes/generate`)
  }

  async get2FARecoveryCodesRegenerate(): Promise<TwoFactorAuthRecoveryCodesDto> {
    return this.apiClient.get(`/2fa/recoverycodes/regenerate`)
  }

  async isRetakeClassificationTestEnabled(subCategoryId: number): Promise<boolean> {
    return this.apiClient.get(`/account/retake-classification-test?subcategoryId=${subCategoryId}`)
  }

  async getPublicDomainByTickmillCompany(
    tickmillCompany: number,
    domain: string
  ): Promise<PublicDomainDto> {
    return this.apiClient.get(`/public-domain/${tickmillCompany}?domain=${domain}`)
  }

  async updateClientLegalDocuments(values: AgreedClientDocumentDto): Promise<void> {
    return this.apiClient.put<void>('/account/legal-documents', values)
  }

  async getMarketDataClassifications(locale: string): Promise<NameDto[]> {
    return this.apiClient.get<NameDto[]>(`/market-data-classifications?languageId=${locale}`)
  }

  async getTradingAccountCreationOptions(
    campaignId: string,
    params?: {
      platformTypeId?: string
    }
  ): Promise<TradingAccountCreationOptions> {
    if (params?.platformTypeId) {
      return this.apiClient.get(
        `/campaigns/${campaignId}/for-create-trading-account?${new URLSearchParams(params)}`
      )
    } else {
      return this.apiClient.get(`/campaigns/${campaignId}/for-create-trading-account`)
    }
  }

  async getGeneralStatus(params: {
    clientId?: string
    languageId?: string
  }): Promise<DocumentGeneralStatusDto> {
    return this.apiClient.get(`/documents/general-status?${new URLSearchParams(params)}`)
  }

  async getCreateQR(params: CreateQRParams): Promise<File> {
    const urlSearchParams = new URLSearchParams({
      url: params.url,
      foregroundColor: params.foregroundColor,
      backgroundColor: params.backgroundColor,
      includeLogoInQR: params.includeLogoInQR.toString(),
      imageFormat: params.imageFormat,
      resolution: params.resolution,
      shortenUrl: params.shortenUrl.toString(),
    })

    const response = await this.apiClient.fetch<File>(`/create-qr?${urlSearchParams}`, {
      method: 'GET',
      responseType: 'blob',
    })

    return response.data
  }

  async getIBPerformance(
    params: IBPerformanceParams
  ): Promise<IntroducingBrokerPerformanceChartDetailDto[]> {
    return this.apiClient.get(
      `/introducing-brokers/${params.id}/performance?from=${params.from}&to=${params.to}`
    )
  }

  async getIBLoyalty(params: IBLoyaltyParams): Promise<LoyaltyDashboardDetailsDto> {
    return this.apiClient.get(
      `/introducing-brokers/${params.id}/loyalty?languageId=${params.languageId}`
    )
  }

  async getIBLoyaltyRewardsHistory(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<PageData<LoyaltyRewardsHistoryDto>> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/loyalty-rewards/history?${preparePageQueryParams(
        query
      )}`
    )
  }

  async getIBMultiLevelCommission(id: string): Promise<MultiLevelCommissionDetailDto> {
    return this.apiClient.get(`/introducing-brokers/${id}/multi-level-commission`)
  }

  async dismissIBMultilevelCommissionNotification(id: string): Promise<void> {
    return this.apiClient.post(
      `/introducing-brokers/${id}/multi-level-commission/dismiss-notification`,
      {}
    )
  }

  async getPayoutScheme(ibId: string): Promise<PayoutSchemeDto[]> {
    return this.apiClient.get(`/introducing-brokers/${ibId}/payout-scheme`)
  }

  async getSumsubAccessToken(): Promise<SumsubAccessTokenData> {
    return this.apiClient.get('/verification/access-token')
  }

  async getSumsubStatus(): Promise<SumsubApplicantState> {
    return this.apiClient.get('/sumsub/applicants/get-latest-kyc-state')
  }

  async getIBRebateGroupsSummary(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<RebateGroupsSummaryDto> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/rebate-groups/summary?${preparePageQueryParams(
        query
      )}`
    )
  }

  async getIBRebateTransactionsSummary(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<RebateTransactionsSummaryDto> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/rebate-transactions/summary?${preparePageQueryParams(
        query
      )}`
    )
  }

  async getIBRebateGroups(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<PageData<IntroducingBrokerRebateGroupDto>> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/rebate-groups?${preparePageQueryParams(query)}`
    )
  }

  async getIBRebateTransaction(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<PageData<RebateTransactionDto>> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/rebate-transactions?${preparePageQueryParams(
        query
      )}`
    )
  }

  async createRebateGroup(id: string, values: GroupFormValues): Promise<void> {
    return this.apiClient.post(`/introducing-brokers/${id}/rebate-groups`, values)
  }

  async updateRebateGroup(
    introducingBrokerId: string,
    groupId: string,
    values: GroupFormValues
  ): Promise<void> {
    return this.apiClient.put(
      `/introducing-brokers/${introducingBrokerId}/rebate-groups/${groupId}`,
      {
        introducingBrokerId: introducingBrokerId,
        id: groupId,
        ...values,
      }
    )
  }

  async deleteRebateGroup(introducingBrokerId: string, groupId: string): Promise<void> {
    return this.apiClient.delete(
      `/introducing-brokers/${introducingBrokerId}/rebate-groups/${groupId}`
    )
  }

  async getRebateApprovalTypes(): Promise<NameDto<RebateApprovalType>[]> {
    return this.apiClient.get(`/introducing-brokers/rebate-approval-types`)
  }

  async getRebateApprovalSummary(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<RebateApprovalSummary> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/rebate-payouts/summary?${preparePageQueryParams(
        query
      )}`
    )
  }

  async getIBRebateApprovals(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<PageData<IntroducingBrokerRebateApprovalDto>> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/rebate-payouts?${preparePageQueryParams(query)}`
    )
  }

  async approveSingleRebate(
    introducingBrokerId: string,
    approvalId: string
  ): Promise<IBRebateSingleApprovalResponseDto> {
    return this.apiClient.put(
      `/introducing-brokers/${introducingBrokerId}/rebate-payouts/${approvalId}/approve`,
      {}
    )
  }

  async approveBulkRebates(
    introducingBrokerId: string,
    approvalIds: string[]
  ): Promise<IBRebateBulkApprovalResponseDto> {
    return this.apiClient.put(
      `/introducing-brokers/${introducingBrokerId}/rebate-payouts/approve`,
      {
        approvalIds,
      }
    )
  }

  async rejectRebate(introducingBrokerId: string, approvalId: string): Promise<void> {
    return this.apiClient.put(
      `/introducing-brokers/${introducingBrokerId}/rebate-payouts/${approvalId}/reject`,
      {}
    )
  }

  async rejectBulkRebates(
    introducingBrokerId: string,
    approvalIds: string[]
  ): Promise<IBRebateBulkApprovalResponseDto> {
    return this.apiClient.put(`/introducing-brokers/${introducingBrokerId}/rebate-payouts/reject`, {
      approvalIds,
    })
  }

  async recalculateRebate(
    introducingBrokerId: string,
    approvalId: string,
    rebateAmountInPercentage: number
  ): Promise<void> {
    return this.apiClient.put(
      `/introducing-brokers/${introducingBrokerId}/rebate-payouts/${approvalId}`,
      { rebateAmountInPercentage }
    )
  }

  async getIBTradingAccountsRebateSummary(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<TradingAccountRebateDto> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/trading-accounts/rebates/summary?${preparePageQueryParams(
        query
      )}`
    )
  }

  async getIBTradingAccountsRebate(
    introducingBrokerId: string,
    query?: PageQuery
  ): Promise<PageData<TradingAccountRebateDetailedDto>> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/trading-accounts/rebates?${preparePageQueryParams(
        query
      )}`
    )
  }

  async updateIBTradingAccountsRebates(
    body: GetUpdateIntroducingBrokerTradingAccountRebateBulk
  ): Promise<{ processed: NameDto[]; skipped: NameDto[] }> {
    return this.apiClient.put(
      `/introducing-brokers/${body.introducingBrokerId}/trading-accounts/rebates`,
      body
    )
  }

  async addIBTradingAccountsRebateGroups(
    body: CreateTradingAccountIntroducingBrokerRebateGroupBulk
  ): Promise<void> {
    return this.apiClient.post(
      `/introducing-brokers/${body.introducingBrokerId}/trading-accounts/rebate-groups`,
      body
    )
  }

  async deleteIBTradingAccountsRebates(params: {
    introducingBrokerId: string
    tradingAccountId: string
  }): Promise<void> {
    const { introducingBrokerId, tradingAccountId } = params
    return this.apiClient.delete(
      `/introducing-brokers/${introducingBrokerId}/trading-accounts/${tradingAccountId}/rebates`
    )
  }

  async deleteIBTradingAccountsRebatesBulk(
    introducingBrokerId: string,
    tradingAccountIds: string[]
  ): Promise<void> {
    return this.apiClient.delete(
      `/introducing-brokers/${introducingBrokerId}/trading-accounts/rebates`,
      {
        tradingAccountIds,
      }
    )
  }

  async deleteIBTradingAccountsRebateGroups(params: {
    introducingBrokerId: string
    tradingAccountId: string
    groupId: string
  }): Promise<void> {
    const { introducingBrokerId, tradingAccountId, groupId } = params
    return this.apiClient.delete(
      `/introducing-brokers/${introducingBrokerId}/trading-accounts/${tradingAccountId}/rebate-groups/${groupId}`
    )
  }

  async getIBTradingAccountsRebatesFilterOptions(
    introducingBrokerId: string
  ): Promise<FilterOptionsForIntroducingBrokerRebatesDto> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/trading-accounts/rebates/filter-options`
    )
  }

  async getIBRebatePayoutsFilterOptions(
    introducingBrokerId: string
  ): Promise<FilterOptionsForIntroducingBrokerRebatePayoutsDto> {
    return this.apiClient.get(
      `/introducing-brokers/${introducingBrokerId}/rebate-payouts/filter-options`
    )
  }

  async getReferralMaterialFilterOptions(
    locale: Locale,
    domain: string
  ): Promise<ReferralMaterialFilterOptionsDto> {
    return this.apiClient.get(
      `/referral-materials/filter-options?languageId=${locale}&domain=${domain}`
    )
  }

  async getReferralMaterial(
    referralMaterialId: string,
    locale: Locale,
    domain: string,
    query?: PageQuery
  ): Promise<ReferralMaterialPreviewDto> {
    return this.apiClient.get(
      `/referral-materials/${referralMaterialId}?languageId=${locale}&domain=${domain}&${preparePageQueryParams(
        query
      )}`
    )
  }

  async generateReferralUrl({
    referralMaterialId,
    defaultUrlId,
    customUrl,
    ibCode,
    domain,
    shortenUrl,
  }: ReferralMaterialsGenerateUrlParams): Promise<{ shortenUrl: string; url: string }> {
    const queryUrl = defaultUrlId
      ? `url=${defaultUrlId}&`
      : customUrl
      ? `customUrl=${customUrl}&`
      : ''

    return this.apiClient.get(
      `referral-materials/${referralMaterialId}/referral-url?${queryUrl}IntroducingBrokerCode=${ibCode}&domain=${domain}&GenerateShortenUrl=${shortenUrl}`
    )
  }

  async getCustomerSupportContacts(
    locale: Locale,
    tickmillCompanyId: TickmillCompaniesEnum | null,
    tickmillProductId: number | undefined
  ): Promise<CustomerSupportContact[]> {
    let url = `/customer-support-contacts?fetchWithLogos=true&languageId=${locale}`

    if (tickmillCompanyId !== undefined) {
      url += `&tickmillCompanyId=${tickmillCompanyId}`
    }

    if (tickmillProductId !== undefined) {
      url += `&tickmillProductId=${tickmillProductId}`
    }

    return await this.apiClient.get(url)
  }

  async getLocalizedVideos(locale: Locale, category = 'tradingview'): Promise<VideoData[]> {
    return await this.apiClient.get(`/videos?category=${category}&languageId=${locale}`)
  }

  async getClientDashboardLayouts(
    dashboardTypeIds?: number[],
    languageId?: string
  ): Promise<ClientDashboardLayoutDto[]> {
    const params = new URLSearchParams()

    dashboardTypeIds?.forEach((id) => params.append('dashboardTypeIds', String(id)))
    if (languageId) params.append('languageId', languageId)

    const queryString = params.toString()
    return this.apiClient.get(`/account/layouts${queryString ? `?${queryString}` : ''}`)
  }

  async getAvailableDashboardLayouts(
    dashboardTypeIds?: number[],
    languageId?: string
  ): Promise<AvailableDashboardTypeDto[]> {
    const params = new URLSearchParams()

    dashboardTypeIds?.forEach((id) => params.append('dashboardTypeIds', String(id)))
    if (languageId) params.append('languageId', languageId)

    const queryString = params.toString()
    return this.apiClient.get(`/account/available-layouts${queryString ? `?${queryString}` : ''}`)
  }
  async createDashboardLayout(
    payload: CreateDashboardLayoutPayload
  ): Promise<ClientDashboardLayoutDto> {
    return this.apiClient.post('/account/layouts', payload)
  }

  async updateDashboardLayout(
    layoutId: string,
    payload: UpdateDashboardLayoutPayload
  ): Promise<ClientDashboardLayoutDto> {
    return this.apiClient.put(`/account/layouts/${layoutId}`, payload)
  }

  async deleteDashboardLayout(layoutId: string): Promise<void> {
    return this.apiClient.delete(`/account/layouts/${layoutId}`, {})
  }

  async getCJMTotals(params: IntroducingBrokerCJMParams): Promise<IntroducingBrokerCJMDto> {
    return this.apiClient.get(
      `/introducing-brokers/${params.id}/reports/clients/client-journey-map/totals`
    )
  }

  async getNVMetrics(
    introducingBrokerId: string,
    from: string,
    to: string
  ): Promise<MetricsNVDto[]> {
    return this.apiClient.get(
      `stats/nv-traded?introducingBrokerId=${introducingBrokerId}&from=${from}&to=${to}`
    )
  }

  async getLotsMetrics(
    introducingBrokerId: string,
    from: string,
    to: string
  ): Promise<MetricsLotsDto[]> {
    return this.apiClient.get(
      `stats/lots?introducingBrokerId=${introducingBrokerId}&from=${from}&to=${to}`
    )
  }

  async getFTDMetrics(
    introducingBrokerId: string,
    from: string,
    to: string
  ): Promise<MetricsCountDto[]> {
    return this.apiClient.get(
      `stats/first-time-deposit?introducingBrokerId=${introducingBrokerId}&from=${from}&to=${to}`
    )
  }

  async getDepositsMetrics(
    introducingBrokerId: string,
    from: string,
    to: string
  ): Promise<MetricsDepositsDto[]> {
    return this.apiClient.get(
      `stats/deposits?introducingBrokerId=${introducingBrokerId}&from=${from}&to=${to}`
    )
  }

  async getNetDepositsMetrics(
    introducingBrokerId: string,
    from: string,
    to: string
  ): Promise<MetricsNetDepositsDto[]> {
    return this.apiClient.get(
      `stats/net-deposits?introducingBrokerId=${introducingBrokerId}&from=${from}&to=${to}`
    )
  }

  async getNewClientsMetrics(
    introducingBrokerId: string,
    from: string,
    to: string
  ): Promise<MetricsCountDto[]> {
    return this.apiClient.get(
      `stats/new-clients?introducingBrokerId=${introducingBrokerId}&from=${from}&to=${to}`
    )
  }

  async getActiveClientsMetrics(introducingBrokerId: string): Promise<MetricsCountDto> {
    return this.apiClient.get(`stats/active-clients?introducingBrokerId=${introducingBrokerId}`)
  }

  async getOpenedTAMetrics(
    introducingBrokerId: string,
    from: string,
    to: string
  ): Promise<MetricsCountDto[]> {
    return this.apiClient.get(
      `stats/trading-accounts-opened?introducingBrokerId=${introducingBrokerId}&from=${from}&to=${to}`
    )
  }

  async getActiveTAMetrics(introducingBrokerId: string): Promise<MetricsCountDto> {
    return this.apiClient.get(
      `stats/active-trading-accounts?introducingBrokerId=${introducingBrokerId}`
    )
  }

  async getIBCommissionsMetrics(
    introducingBrokerId: string,
    from: string,
    to: string
  ): Promise<MetricsIBCommissionsDto[]> {
    return this.apiClient.get(
      `stats/commissions?introducingBrokerId=${introducingBrokerId}&from=${from}&to=${to}`
    )
  }

  async getRewardsHubDashboard(): Promise<RewardsHubDashboardDto> {
    return this.apiClient.get(`/rewards-hub-dashboard`)
  }

  async getRewardsHubRewardsHistory(
    query?: PageQuery
  ): Promise<PageData<RewardsHubRewardsHistoryDto>> {
    return await this.apiClient.get<PageData<RewardsHubRewardsHistoryDto>>(
      `/trading-view-coupon-history?${preparePageQueryParams(query)}`
    )
  }

  async getMarginBonus(
    tradingAccountId: string | undefined,
    campaignId: string | undefined,
    amount: number | undefined
  ): Promise<MarginBonusDto> {
    return this.apiClient.get(
      `/trading-accounts/${tradingAccountId}/calculate-margin-bonus?amount=${amount}&campaignId=${campaignId}`
    )
  }

  async getRewardsHubCampaignsHistory(
    query?: PageQuery
  ): Promise<PageData<RewardsHubCampaignsHistoryDto>> {
    return this.apiClient.get<PageData<RewardsHubCampaignsHistoryDto>>(
      `/campaign-history?${preparePageQueryParams(query)}`
    )
  }

  async getTopMoversRisers(options: {
    periodType?: number | null
    assetType?: string | null
  }): Promise<RisersDto[]> {
    const { periodType, assetType } = options
    const params = new URLSearchParams()
    if (periodType) params.append('periodType', periodType.toString())
    if (assetType) params.append('category', assetType)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return this.apiClient.get<RisersDto[]>(`/platform/analytics/top-movers/risers${qs}`)
  }

  async getTopMoversFallers(options: {
    periodType?: number | null
    assetType?: string | null
  }): Promise<FallersDto[]> {
    const { periodType, assetType } = options
    const params = new URLSearchParams()
    if (periodType) params.append('periodType', periodType.toString())
    if (assetType) params.append('category', assetType)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return this.apiClient.get<FallersDto[]>(`/platform/analytics/top-movers/fallers${qs}`)
  }

  async getTopTradedBought(options: {
    periodType?: number | null
    assetType?: string | null
  }): Promise<TopTradedBoughtDto[]> {
    const { periodType, assetType } = options
    const params = new URLSearchParams()
    if (periodType) params.append('periodType', periodType.toString())
    if (assetType) params.append('Category', assetType)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return this.apiClient.get<TopTradedBoughtDto[]>(`/platform/analytics/top-traded/bought${qs}`)
  }

  async getTopTradedSold(options: {
    periodType?: number | null
    assetType?: string | null
  }): Promise<TopTradedSoldDto[]> {
    const { periodType, assetType } = options
    const params = new URLSearchParams()
    if (periodType) params.append('periodType', periodType.toString())
    if (assetType) params.append('category', assetType)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return this.apiClient.get<TopTradedSoldDto[]>(`/platform/analytics/top-traded/sold${qs}`)
  }

  async getMostVolatile(options: {
    periodType?: number | null
    assetType?: string | null
  }): Promise<MostVolatileDto[]> {
    const { periodType, assetType } = options
    const params = new URLSearchParams()
    if (periodType) params.append('periodType', periodType.toString())
    if (assetType) params.append('category', assetType)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return this.apiClient.get<MostVolatileDto[]>(`/platform/analytics/most-volatile${qs}`)
  }

  async getMostFollowed(options: { assetType?: string | null }): Promise<MostFollowedDto[]> {
    const { assetType } = options
    const params = new URLSearchParams()
    if (assetType) params.append('category', assetType)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return this.apiClient.get<MostFollowedDto[]>(`/platform/analytics/most-followed${qs}`)
  }

  async createReferralLinkAlias(
    payload: CreateReferralLinkAliasDto
  ): Promise<CreateReferralLinkAliasDtoResponse> {
    return this.apiClient.post<CreateReferralLinkAliasDtoResponse>(
      '/referral-materials/link-hub-alias',
      payload
    )
  }

  async getReferralLinkAliases(
    introducingBrokerId: string,
    mainIbSchemeId: number,
    query?: PageQuery
  ): Promise<PageData<ReferralLinkAliasesDto>> {
    const params = preparePageQueryParams(query)

    params.set('search', `MainIbSchemeId == ${mainIbSchemeId}`)

    return this.apiClient.get(
      `/referral-materials/link-hub-alias/${introducingBrokerId}?${params.toString()}`
    )
  }

  async updateReferralLinkAlias(id: string, payload: UpdateReferralLinkAliasDto): Promise<void> {
    return this.apiClient.put(`/referral-materials/link-hub-alias/${id}`, payload)
  }

  async getExportHubReports(ibId: string, query?: PageQuery): Promise<PageData<ExportHubDto>> {
    return this.apiClient.get<PageData<ExportHubDto>>(
      `/introducing-brokers/${ibId}/export-hub?${preparePageQueryParams(query)}`
    )
  }

  async getExportHubFilterOptions(ibId: string, languageId: string): Promise<FilterOptionsDto> {
    return this.apiClient.get<FilterOptionsDto>(
      `/introducing-brokers/${ibId}/export-hub/filter-options?languageId=${languageId}`
    )
  }

  async createExportFile(ibId: string, payload: ExportHubPayload): Promise<{ id: string }> {
    return this.apiClient.post<{ id: string }>(`/introducing-brokers/${ibId}/export-hub`, payload)
  }

  async retryExportHubReport(ibId: string, reportId: string): Promise<void> {
    return this.apiClient.post<void>(
      `/introducing-brokers/${ibId}/export-hub/${reportId}/retry`,
      {}
    )
  }

  async getExportHubHasUnseen(ibId: string): Promise<{ hasUnseen: boolean }> {
    return this.apiClient.get<{ hasUnseen: boolean }>(
      `/introducing-brokers/${ibId}/export-hub/has-unseen`
    )
  }

  async exportHubAcknowledge(ibId: string, payload: { reportIds: string[] }): Promise<void> {
    return this.apiClient.post<void>(`/introducing-brokers/${ibId}/export-hub/acknowledge`, payload)
  }

  async checkSecurityBreach(
    password: string,
    abortController?: Partial<AbortController>
  ): Promise<boolean> {
    return this.apiClient.post(
      '/password-reset/validation-rules/validate-by-name',
      {
        name: 'SecurityBreachScreening',
        value: password,
      },
      { signal: abortController?.signal }
    )
  }
}
